import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  IconButton,
  Snackbar,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import { 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend 
} from 'recharts';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EmailIcon from '@mui/icons-material/Email';
import SecurityIcon from '@mui/icons-material/Security';
import DnsIcon from '@mui/icons-material/Dns';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import SettingsIcon from '@mui/icons-material/Settings';
import TimelineIcon from '@mui/icons-material/Timeline';
import ErrorIcon from '@mui/icons-material/Error';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import CategoryIcon from '@mui/icons-material/Category';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import RecentActorsIcon from '@mui/icons-material/RecentActors';
import CodeIcon from '@mui/icons-material/Code';
import DescriptionIcon from '@mui/icons-material/Description';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import { List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import logger from './../utils/logger.js';

const DNSStatistics = () => {
  // State variables
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [generalStats, setGeneralStats] = useState(null);
  const [emailProviderStats, setEmailProviderStats] = useState([]);
  const [dmarcPolicyStats, setDmarcPolicyStats] = useState([]);
  const [spfProviderStats, setSpfProviderStats] = useState([]);
  const [dmarcRuaStats, setDmarcRuaStats] = useState([]);
  const [filteredSpfProviders, setFilteredSpfProviders] = useState(null);
  const [filteredDmarcRua, setFilteredDmarcRua] = useState(null);
  const [mxProviderStats, setMxProviderStats] = useState([]);
  const [filteredMxProviders, setFilteredMxProviders] = useState(null);
  const [groupMxProviders, setGroupMxProviders] = useState(true);
  const [verificationStats, setVerificationStats] = useState(null);
  const [filteredVerificationServices, setFilteredVerificationServices] = useState(null);
  const { isAdmin, isSupervisor } = useAuth();
  
  // New state variables for DNS verification pattern management
  const [categoriesList, setCategoriesList] = useState([]);
  const [patternsList, setPatternsList] = useState([]);
  const [recentPatterns, setRecentPatterns] = useState([]);
  const [filteredPatterns, setFilteredPatterns] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [patternDialogOpen, setPatternDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [currentPattern, setCurrentPattern] = useState(null);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [patternFormError, setPatternFormError] = useState(null);
  const [categoryFormError, setCategoryFormError] = useState(null);
  const [patternDialogMode, setPatternDialogMode] = useState('add'); // 'add' or 'edit'
  const [categoryDialogMode, setCategoryDialogMode] = useState('add'); // 'add' or 'edit'
  const [recentDays, setRecentDays] = useState(7);
  const [loadingPatterns, setLoadingPatterns] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [confirmDeleteDialog, setConfirmDeleteDialog] = useState({
    open: false,
    type: '', // 'pattern' or 'category'
    id: null,
    name: ''
  });
  
  // Bulk check state variables
  const [bulkStats, setBulkStats] = useState(null);
  const [bulkCheckLoading, setBulkCheckLoading] = useState(false);
  const [bulkCheckError, setBulkCheckError] = useState(null);
  const [bulkCheckOperations, setBulkCheckOperations] = useState([]);
  const [activeBulkOperation, setActiveBulkOperation] = useState(null);
  const statusPollingIntervalRef = useRef(null);
  const [startBulkCheckDialogOpen, setStartBulkCheckDialogOpen] = useState(false);
  const [maxDomainsToCheck, setMaxDomainsToCheck] = useState(1000); // 1000 as default (maximum allowed by backend)
  const [batchSize, setBatchSize] = useState(10);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isPolling, setIsPolling] = useState(false); // Track if we're actively polling
  
  // New state variables for sequential checking
  const [sequentialMode, setSequentialMode] = useState(false);
  const [sequentialStats, setSequentialStats] = useState({
    totalOperations: 0,
    completedOperations: 0,
    totalDomainsToCheck: 0,
    totalDomainsChecked: 0,
    currentOperation: null,
    remainingDomains: 0,
    isActive: false
  });

  // New state variables for URL rechecks
  const [checkMode, setCheckMode] = useState('new');
  const [daysOld, setDaysOld] = useState(30);

  // Add a ref for the polling state to avoid race conditions
  const isPollingRef = useRef(false);

  // Constants
  const COLORS = [
    '#0088FE', '#00C49F', '#FFBB28', '#FF8042', // Primary colors
    '#8884d8', '#82ca9d', '#ffc658', '#a4de6c', // Secondary colors
    '#d0ed57', '#83a6ed', '#8dd1e1', '#5C7BD9'  // Additional colors
  ];

  const DMARC_POLICY_COLORS = {
    "reject": "#4caf50",      // Green - Strong protection
    "quarantine": "#ff9800",  // Orange - Medium protection
    "none": "#f44336",        // Red - No protection
    "No DMARC": "#9e9e9e"     // Grey - Missing
  };

  const EMAIL_PROVIDER_COLORS = {
    "M365": "#0078d4",           // Microsoft Blue
    "M365_Filtered": "#00a2ed",  // Lighter Microsoft Blue
    "Google": "#ea4335",         // Google Red
    "Other": "#fbbc05",          // Yellow
    "Unknown": "#9e9e9e"         // Grey
  };

  const SPF_PROVIDER_COLORS = {
    "protection.outlook.com": "#0078D4",  // Microsoft Blue
    "google.com": "#4285F4",             // Google Blue
    "office365.com": "#0078D4",          // Microsoft Blue
    "spf.protection.outlook.com": "#0078D4", // Microsoft Blue
    "amazonses.com": "#FF9900",          // Amazon Orange
    "sendgrid.net": "#1A82E2",           // SendGrid Blue
    "mailchimp.com": "#FFE01B",          // Mailchimp Yellow
    "mailgun.org": "#F0616F",            // Mailgun Red
    "zoho.com": "#F44336",               // Zoho Red
    "mandrillapp.com": "#2C9AB7"         // Mandrill Blue
  };

  const RUA_PROVIDER_COLORS = {
    "emaildefense.proofpoint.com": "#5E35B1",    // Deep Purple
    "dmarcanalyzer.com": "#00897B",              // Teal
    "dmarc.barracudanetworks.com": "#F57C00",    // Orange
    "dmarc.postmarkapp.com": "#C2185B",          // Pink
    "dmarc.mimecast.com": "#1976D2",             // Blue
    "dmarc.eu.sparkpostmail.com": "#D32F2F",     // Red
    "Self": "#4CAF50",                           // Green
    "rua.agari.com": "#7B1FA2"                   // Purple
  };

  const MX_PROVIDER_COLORS = {
    "Self-hosted": "#4CAF50",      // Green for self-hosted
    "spamexperts.com": "#FF9800",  // Orange
    "yourfilter.nl": "#1976D2",    // Blue
    "mimecast.com": "#9C27B0",     // Purple
    "mtaroutes.com": "#00BCD4",    // Cyan
    "idefender.nl": "#795548",     // Brown
    "transip.nl": "#607D8B",       // Blue Grey
    "hostnet.nl": "#E91E63",       // Pink
    "filtermail.eu": "#FF5722",    // Deep Orange
    "ppe-hosted.com": "#3F51B5",   // Indigo
    "one.com": "#2196F3",          // Light Blue
    "hornetsecurity.com": "#009688", // Teal
    "onlinemailscanner.com": "#CDDC39", // Lime
    "solcon.nl": "#8BC34A",        // Light Green
    "mailfilter.hostnet.nl": "#E91E63", // Pink (same as hostnet.nl)
    "fallbackmx.spamexperts.eu": "#FF9800", // Orange (same as spamexperts.com)
    "lastmx.spamexperts.net": "#FF9800" // Orange (same as spamexperts.com)
  };

  const VERIFICATION_SERVICE_COLORS = {
    "Google": "#4285F4", // Google Blue
    "Microsoft": "#0078D4", // Microsoft Blue
    "Apple": "#A2AAAD", // Apple Silver
    "OpenAI": "#10A37F", // OpenAI Green
    "Zoom": "#2D8CFF", // Zoom Blue
    "Slack": "#4A154B", // Slack Purple
    "Stripe": "#635BFF", // Stripe Blue
    "Atlassian": "#0052CC", // Atlassian Blue
    "Shopify": "#7AB55C", // Shopify Green
    "Facebook": "#1877F2", // Facebook Blue
    "Airtable": "#F82B60", // Airtable Pink
    "Canva": "#00C4CC", // Canva Teal
    "Intercom": "#286EFA", // Intercom Blue
    "Cursor": "#00E5B7", // Cursor Mint
    "Wiz Security": "#2E5BFF", // Wiz Blue
    "Unknown": "#9E9E9E" // Grey for unknown
  };

  // Load data from the API
  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load general statistics
      const generalResponse = await api.getDnsGeneralStats();
      if (generalResponse.success) {
        setGeneralStats(generalResponse.data);
      } else {
        setError(generalResponse.message || 'Failed to load general DNS statistics');
      }

      // Load email provider statistics
      const emailResponse = await api.getDnsEmailProviderStats();
      if (emailResponse.success) {
        setEmailProviderStats(emailResponse.data || []);
      } else {
        setError(emailResponse.message || 'Failed to load email provider statistics');
      }

      // Load DMARC policy statistics
      const dmarcResponse = await api.getDnsDmarcPolicyStats();
      if (dmarcResponse.success) {
        setDmarcPolicyStats(dmarcResponse.data || []);
      } else {
        setError(dmarcResponse.message || 'Failed to load DMARC policy statistics');
      }

      // Load SPF provider statistics
      const spfProviderResponse = await api.getDnsSpfProviderStats();
      if (spfProviderResponse.success) {
        setSpfProviderStats(spfProviderResponse.data);
      } else {
        setError(spfProviderResponse.message || 'Failed to load SPF provider statistics');
      }
      
      // Load DMARC RUA statistics
      const dmarcRuaResponse = await api.getDnsDmarcRuaStats();
      if (dmarcRuaResponse.success) {
        setDmarcRuaStats(dmarcRuaResponse.data);
      } else {
        setError(dmarcRuaResponse.message || 'Failed to load DMARC RUA statistics');
      }

      // Load MX provider statistics
      const mxProviderResponse = await api.getDnsMxProviderStats();
      if (mxProviderResponse.success) {
        setMxProviderStats(mxProviderResponse.data || []);
      } else {
        setError(mxProviderResponse.message || 'Failed to load MX provider statistics');
      }

      // Load verification service statistics
      const verificationResponse = await api.getDnsVerificationStats();
      if (verificationResponse.success) {
        setVerificationStats(verificationResponse.data || null);
      } else {
        setError(verificationResponse.message || 'Failed to load verification service statistics');
      }
    } catch (err) {
      logger.error('Error loading DNS statistics:', err);
      setError('Failed to load DNS statistics');
    } finally {
      setLoading(false);
    }
  };
  
  // Load bulk DNS check statistics
  const loadBulkStats = async () => {
    setBulkCheckLoading(true);
    setBulkCheckError(null);
    
    try {
      const response = await api.getDnsBulkStats();
      if (response.success) {
        setBulkStats(response.data);
      }
      
      // Also load any existing bulk check operations
      await loadBulkCheckOperations();
    } catch (err) {
      logger.error('Error loading bulk DNS check statistics:', err);
      setBulkCheckError('Failed to load bulk DNS check statistics');
    } finally {
      setBulkCheckLoading(false);
    }
  };
  
  // Load bulk check operations without starting polling
  const loadBulkCheckOperations = async () => {
    try {
      const response = await api.getDnsBulkCheckOperations();
      if (response.success) {
        setBulkCheckOperations(response.data || []);
        
        // Find any active operations (running or queued)
        const activeOp = (response.data || []).find(op => 
          op.status === 'running' || op.status === 'queued'
        );
        
        if (activeOp) {
          // Only update the active operation if we don't already have one
          // or if the status has changed
          if (!activeBulkOperation || 
              activeBulkOperation.id !== activeOp.id || 
              activeBulkOperation.status !== activeOp.status) {
            setActiveBulkOperation(activeOp);
            
            // Only start polling if we aren't already polling
            if (!statusPollingIntervalRef.current) {
              startStatusPolling(activeOp.id);
            }
          }
        } else {
          // Only clear the active operation if we have one
          if (activeBulkOperation) {
            setActiveBulkOperation(null);
          }
          // Stop polling if there's no active operation
          stopStatusPolling();
        }
      }
    } catch (err) {
      logger.error('Error loading bulk check operations:', err);
      // Don't set error to avoid blocking the UI
    }
  };
  
  // Update the startStatusPolling function to use a faster, more aggressive polling for new operations
  const startStatusPolling = (operationId, isNewOperation = false) => {
    // Clean log message
    logger.log('%c🔄 STARTING NEW POLLING', 'background: #ff0000; color: white; font-size: 14px; font-weight: bold; padding: 5px;');
    
    // Always stop any existing polling first
    stopStatusPolling();
    
    // Set polling to active
    setIsPolling(true);
    
    // Use a straightforward approach - check very frequently for new operations (100ms)
    // and gradually increase the interval as the operation continues
    let interval = isNewOperation ? 100 : 2000; // Start with 100ms for new ops, 2s for existing
    
    // Function to execute a single status check
    const checkOnce = async () => {
      if (!isPolling) {
        logger.log('Polling was stopped, aborting check');
        return;
      }
      
      logger.log(`Checking status for operation ${operationId} (interval: ${interval}ms)`);
      
      try {
        const response = await api.getDnsBulkCheckStatus(operationId);
        
        if (response && response.success && response.data) {
          const status = response.data;
          
          // Always update the UI
          setActiveBulkOperation(status);
          
          logger.log(`Status: ${status.status}, Progress: ${status.percent_complete}%`);
          
          // Check if the operation is done
          if (
            status.status === 'completed' || 
            status.status === 'error' || 
            status.status === 'cancelled' || 
            (status.status === 'running' && status.percent_complete >= 100)
          ) {
            // Operation is complete
            logger.log(`Operation ${operationId} is ${status.status}, stopping polling`);
            
            // Stop polling
            setIsPolling(false);
            
            // Update UI with the final status
            refreshDataAfterCompletion(status);
            return;
          }
          
          // If operation is still ongoing, schedule next check
          if (status.status === 'running') {
            // For running ops, use a medium interval (1s)
            interval = 1000;
          } else if (status.status === 'queued' && isNewOperation) {
            // For queued new ops, keep checking frequently (100ms)
            interval = 100;
          } else {
            // For all other cases, use the standard interval (2s)
            interval = 2000;
          }
          
          // Schedule next check if the operation is still active
          if (isPolling) {
            setTimeout(checkOnce, interval);
          }
        } else {
          logger.error('Invalid response:', response);
          // Retry after standard interval
          if (isPolling) {
            setTimeout(checkOnce, 2000);
          }
        }
      } catch (error) {
        logger.error('Error checking status:', error);
        // Retry after standard interval despite error
        if (isPolling) {
          setTimeout(checkOnce, 2000);
        }
      }
    };
    
    // Start the polling process immediately
    checkOnce();
  };
  
  // Update the stopStatusPolling function to be simpler as well
  const stopStatusPolling = () => {
    logger.log('Stopping polling');
    
    // Just set the flag to false (the checkOnce function will handle the rest)
    setIsPolling(false);
    
    // Clear the interval reference as well for safety
    if (statusPollingIntervalRef.current) {
      clearInterval(statusPollingIntervalRef.current);
      statusPollingIntervalRef.current = null;
    }
  };

  // Helper function to refresh data after an operation completes
  const refreshDataAfterCompletion = async (operationStatus) => {
    logger.log('Refreshing data after operation completion', operationStatus);
    
    try {
      // First update the bulk stats
      const statsResponse = await api.getDnsBulkStats();
      if (statsResponse.success) {
        setBulkStats(statsResponse.data);
      }
      
      // Then update the operations list
      const opsResponse = await api.getDnsBulkCheckOperations();
      if (opsResponse.success) {
        setBulkCheckOperations(opsResponse.data || []);
      }
      
      // Clear the active operation since it's completed
      setActiveBulkOperation(null);
      
      // Show appropriate completion message based on operation status
      if (operationStatus.status === 'completed') {
        // Check if this was part of a backend sequential operation
        if (operationStatus.is_sequential) {
          // This was part of a sequential operation handled by the backend
          if (operationStatus.is_sequence_complete) {
            // Entire sequence is complete
            showSnackbar(`Sequential bulk DNS check completed: ${operationStatus.total_sequence_domains || operationStatus.domains_checked} domains checked`, 'success');
            
            // Update sequential stats for UI display
            if (sequentialStats.isActive) {
              setSequentialStats({
                ...sequentialStats,
                isActive: false,
                completedOperations: sequentialStats.totalOperations,
                totalDomainsChecked: operationStatus.total_sequence_domains || sequentialStats.totalDomainsToCheck,
                remainingDomains: 0
              });
            }
          } else {
            // Just a batch in a sequence completed, backend is still processing
            showSnackbar(`Sequential batch ${operationStatus.sequence_position || '?'} of ${operationStatus.sequence_total || '?'} completed. Backend is processing the remaining batches...`, 'info');
            
            // Update sequential stats for UI if we have the information
            if (sequentialStats.isActive && operationStatus.sequence_position && operationStatus.sequence_total) {
              setSequentialStats({
                ...sequentialStats,
                completedOperations: operationStatus.sequence_position,
                totalOperations: operationStatus.sequence_total,
                totalDomainsChecked: operationStatus.sequence_domains_checked || sequentialStats.totalDomainsChecked + operationStatus.domains_checked,
                remainingDomains: operationStatus.sequence_domains_remaining || Math.max(0, sequentialStats.remainingDomains - operationStatus.domains_checked)
              });
            }
          }
        } else {
          // Standard single operation completion
          showSnackbar(`Bulk DNS check completed: ${operationStatus.domains_checked} domains checked`, 'success');
        }
      } else if (operationStatus.status === 'error') {
        // Show error message
        showSnackbar(`Bulk DNS check failed: ${operationStatus.error || 'Unknown error'}`, 'error');
        
        // If in sequential mode, end it due to error
        if (sequentialStats.isActive) {
          setSequentialStats({
            ...sequentialStats,
            isActive: false
          });
        }
      } else if (operationStatus.status === 'cancelled') {
        // Show cancellation message
        showSnackbar(`Bulk DNS check cancelled`, 'warning');
        
        // If in sequential mode, end it due to cancellation
        if (sequentialStats.isActive) {
          setSequentialStats({
            ...sequentialStats,
            isActive: false
          });
        }
      }
    } catch (error) {
      logger.error('Error refreshing data:', error);
      
      // If in sequential mode, end it due to error
      if (sequentialStats.isActive) {
        setSequentialStats({
          ...sequentialStats,
          isActive: false
        });
        showSnackbar('Error updating status information', 'error');
      }
      
      // Always clear the active operation on error
      setActiveBulkOperation(null);
    }
  };

  // Simplified startBulkCheck function that delegates sequential handling to the backend
  const startBulkCheck = async () => {
    try {
      setBulkCheckLoading(true);
      
      // If this is a sequential operation, get stats to update UI
      if (sequentialMode) {
        // Get fresh stats to calculate number of operations needed
        const statsResponse = await api.getDnsBulkStats();
        if (statsResponse.success) {
          const totalDomains = checkMode === 'new' 
            ? statsResponse.data.records_to_check 
            : (statsResponse.data.domains_to_recheck?.[daysOld] || 0);
          
          // Check if there are domains to process
          if (totalDomains <= 0) {
            showSnackbar('No domains available to check', 'warning');
            setBulkCheckLoading(false);
            return;
          }
          
          const totalOperations = Math.ceil(totalDomains / 1000);
          
          // Initialize sequential stats for UI display only
          setSequentialStats({
            totalOperations,
            completedOperations: 0,
            totalDomainsToCheck: totalDomains,
            totalDomainsChecked: 0,
            remainingDomains: totalDomains,
            isActive: true
          });
          
          // Always use max batch size for sequential operations
          setMaxDomainsToCheck(1000);
          
          showSnackbar(`Starting sequential check of ${totalDomains} domains in ${totalOperations} operations. The backend will process all domains automatically.`, 'info');
        } else {
          throw new Error('Could not retrieve stats to calculate sequential operations');
        }
      }
      
      // Validate domain count
      if (maxDomainsToCheck <= 0) {
        logger.error('Attempted to start check with 0 domains, aborting');
        showSnackbar('Cannot start operation with 0 domains', 'error');
        setBulkCheckLoading(false);
        return;
      }
      
      logger.log('Starting bulk check with params:', { 
        maxDomainsToCheck, 
        batchSize, 
        checkMode, 
        daysOld,
        sequentialMode
      });
      
      // Pass the sequential flag to the backend
      const response = await api.startDnsBulkCheck(
        maxDomainsToCheck, 
        batchSize, 
        checkMode, 
        daysOld, 
        sequentialMode  // Tell backend this is a sequential operation
      );
      
      if (response && response.success) {
        // Close dialog after successful start
        setStartBulkCheckDialogOpen(false);
        
        // Handle the case where no domains need checking (already up to date)
        if (response.already_up_to_date && !response.operation_id) {
          // Show success message and refresh stats but don't start operation tracking
          showSnackbar(response.message || 'All domains are up to date!', 'success');
          
          // Refresh the stats to reflect current state
          refreshStats();
          
          // If in sequential mode, end it since there's nothing to check
          if (sequentialMode && sequentialStats.isActive) {
            setSequentialStats({
              ...sequentialStats,
              isActive: false
            });
          }
          
          return; // Exit early, no operation to track
        }
        
        // Normal case: operation was created and needs tracking
        const operationId = response.operation_id;
        logger.log('Got operation ID:', operationId);
        
        // Validate we have an operation ID for tracking
        if (!operationId) {
          const errorMsg = 'No operation ID received from server';
          logger.error(errorMsg);
          showSnackbar(errorMsg, 'error');
          setBulkCheckError(errorMsg);
          return;
        }
        
        // Show appropriate start message
        showSnackbar(
          sequentialMode 
            ? `Sequential DNS check started - the backend will process all domains` 
            : 'Bulk DNS check started successfully', 
          'success'
        );
        
        // Create initial operation object
        const initialOperation = {
          id: operationId,
          status: 'queued',
          total_domains: parseInt(maxDomainsToCheck),
          domains_checked: 0,
          batch_size: parseInt(batchSize),
          check_mode: checkMode,
          days_old: parseInt(daysOld),
          created_by: sequentialMode ? `You (Sequential)` : 'You',
          start_time: new Date().toISOString(),
          percent_complete: 0,
          is_sequential: sequentialMode,
          error: null
        };
        
        // Update the active operation
        setActiveBulkOperation(initialOperation);
        
        // Start polling for status updates
        forceStartPolling(operationId);
      } else {
        const errorMsg = (response && response.message) || 'Failed to start bulk DNS check';
        setBulkCheckError(errorMsg);
        logger.error('Start bulk check failed:', errorMsg);
        
        // If in sequential mode, end it due to error
        if (sequentialMode && sequentialStats.isActive) {
          setSequentialStats({
            ...sequentialStats,
            isActive: false
          });
          showSnackbar('Sequential operation failed to start: ' + errorMsg, 'error');
        }
      }
    } catch (err) {
      const errorMsg = err.message || 'Unknown error';
      logger.error('Exception in startBulkCheck:', err);
      setBulkCheckError('Failed to start bulk DNS check: ' + errorMsg);
      
      // If in sequential mode, end it due to error
      if (sequentialMode && sequentialStats.isActive) {
        setSequentialStats({
          ...sequentialStats,
          isActive: false
        });
        showSnackbar('Sequential operation failed to start: ' + errorMsg, 'error');
      }
    } finally {
      setBulkCheckLoading(false);
    }
  };
  
  // Add a direct polling start function that bypasses React state update cycles
  const forceStartPolling = (operationId) => {
    logger.log('🔥 FORCE STARTING POLLING for', operationId);
    
    // First, ensure any existing polling is really stopped
    stopPollingImmediately();
    
    // Set ref values directly
    isPollingRef.current = true;
    
    // Define a self-contained polling function that closes over operationId but uses ref for state
    const pollOperation = async () => {
      // Every time we start a check, verify we should still be polling
      if (!isPollingRef.current) {
        logger.log('Polling was stopped, no further checks will be made');
        return;
      }
      
      logger.log(`Checking status for ${operationId}`);
      
      try {
        const response = await api.getDnsBulkCheckStatus(operationId);
        
        if (response && response.success && response.data) {
          const status = response.data;
          
          // Always update the active operation
          setActiveBulkOperation(status);
          
          logger.log(`Status check: ${status.status}, Progress: ${status.percent_complete}%`);
          
          // Check if operation is done
          if (
            status.status === 'completed' || 
            status.status === 'error' || 
            status.status === 'cancelled' || 
            (status.status === 'running' && status.percent_complete >= 100)
          ) {
            logger.log(`Operation ${operationId} is ${status.status}, stopping polling`);
            
            // Stop polling
            isPollingRef.current = false;
            
            // Update the UI
            refreshStats();
            
            return; // No more polling
          }
          
          // Determine next check interval based on status
          let interval = 2000; // Default 2s
          
          if (status.status === 'queued') {
            interval = 100; // Check very frequently for queued operations
          } else if (status.status === 'running') {
            interval = 1000; // Medium frequency for running operations
          }
          
          // Continue polling if still active
          if (isPollingRef.current) {
            setTimeout(pollOperation, interval);
          }
        } else {
          logger.error('Invalid response:', response);
          // Retry after standard interval
          if (isPollingRef.current) {
            setTimeout(pollOperation, 2000);
          }
        }
      } catch (error) {
        logger.error('Error checking status:', error);
        // Retry after standard interval despite error
        if (isPollingRef.current) {
          setTimeout(pollOperation, 2000);
        }
      }
    };
    
    // Immediately start the first poll
    pollOperation();
  };

  // Helper function to stop polling immediately
  const stopPollingImmediately = () => {
    logger.log('🛑 Force stopping all polling');
    isPollingRef.current = false;
    
    // Also reset the React state
    setIsPolling(false);
    
    // Clear any interval for good measure
    if (statusPollingIntervalRef.current) {
      clearInterval(statusPollingIntervalRef.current);
      statusPollingIntervalRef.current = null;
    }
  };
  
  // Add cancelSequentialOperations function
  const cancelSequentialOperations = () => {
    // If there's an active operation, cancel it
    if (activeBulkOperation) {
      cancelBulkCheck(activeBulkOperation.id);
    }
    
    // Update sequential stats to stop further operations
    setSequentialStats({
      ...sequentialStats,
      isActive: false
    });
    
    showSnackbar('Sequential operations cancelled', 'warning');
  };

  // Update the refreshStats function to clear the activeBulkOperation when operations complete
  const refreshStats = async () => {
    logger.log('Refreshing stats and operations list');
    
    try {
      // First update the bulk stats
      const statsResponse = await api.getDnsBulkStats();
      if (statsResponse.success) {
        setBulkStats(statsResponse.data);
      }
      
      // Then update the operations list
      const opsResponse = await api.getDnsBulkCheckOperations();
      if (opsResponse.success) {
        setBulkCheckOperations(opsResponse.data || []);
      }
      
      // Clear the active operation to re-enable the start button
      setActiveBulkOperation(null);
      
    } catch (error) {
      logger.error('Error refreshing data:', error);
    }
  };

  // Also update the cancelBulkCheck function to clear the active operation
  const cancelBulkCheck = async (operationId) => {
    try {
      setBulkCheckLoading(true);
      
      logger.log(`Cancelling operation ${operationId}`);
      
      // Handle the case where operation ID is invalid/undefined (from the bug we just fixed)
      if (!operationId || operationId === 'undefined') {
        logger.log('Invalid operation ID detected, force-clearing the active operation state');
        
        // Stop polling immediately
        stopPollingImmediately();
        
        // Clear the active operation immediately to re-enable the start button
        setActiveBulkOperation(null);
        
        // Show informative message
        showSnackbar('Cleared invalid operation state', 'info');
        
        // Refresh the data
        try {
          const opsResponse = await api.getDnsBulkCheckOperations();
          if (opsResponse.success) {
            setBulkCheckOperations(opsResponse.data || []);
            logger.log('Updated operations list after clearing invalid state');
          }
          
          const statsResponse = await api.getDnsBulkStats();
          if (statsResponse.success) {
            setBulkStats(statsResponse.data);
            logger.log('Updated stats after clearing invalid state');
          }
        } catch (error) {
          logger.error(`Error refreshing data after clearing invalid state: ${error.message}`);
        } finally {
          setBulkCheckLoading(false);
        }
        
        return;
      }
      
      // Normal case: valid operation ID, call the API
      const response = await api.cancelDnsBulkCheck(operationId);
      
      if (response.success) {
        logger.log('Cancel request successful');
        
        // Stop polling immediately
        stopPollingImmediately();
        
        // Show cancellation message
        showSnackbar('Bulk DNS check cancelled successfully', 'warning');
        
        // Clear the active operation immediately to re-enable the start button
        setActiveBulkOperation(null);
        
        // Refresh the data
        setTimeout(async () => {
          try {
            const opsResponse = await api.getDnsBulkCheckOperations();
            if (opsResponse.success) {
              setBulkCheckOperations(opsResponse.data || []);
              logger.log('Updated operations list after cancellation');
            }
            
            const statsResponse = await api.getDnsBulkStats();
            if (statsResponse.success) {
              setBulkStats(statsResponse.data);
              logger.log('Updated stats after cancellation');
            }
          } catch (error) {
            logger.error(`Error refreshing data after cancellation: ${error.message}`);
          } finally {
            setBulkCheckLoading(false);
          }
        }, 1000);
      } else {
        logger.error(`Cancel request failed: ${response.message}`);
        setBulkCheckError(response.message || 'Failed to cancel bulk DNS check');
        setBulkCheckLoading(false);
      }
    } catch (err) {
      logger.error(`Exception cancelling operation: ${err.message}`);
      setBulkCheckError('Failed to cancel bulk DNS check: ' + (err.message || 'Unknown error'));
      setBulkCheckLoading(false);
    }
  };
  
  // Snackbar helper
  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };
  
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  // Load data on component mount and when tab changes
  useEffect(() => {
    logger.log('%c🚀 DNS STATISTICS COMPONENT MOUNTED', 'background: #4CAF50; color: white; font-size: 14px; font-weight: bold; padding: 5px;');
    
    logger.log(`Tab changed to ${tabValue}`);
    
    // Stop any ongoing polling when tab changes
    stopStatusPolling();
    
    // Load data for all tabs except Bulk DNS Checks tab (which is now tab 8)
    if (tabValue >= 0 && tabValue <= 6) {
      loadData();
    } else if (tabValue === 7) {
      // Pattern Management tab - load categories and patterns
      logger.log('Loading pattern management tab data');
      loadCategories();
      loadPatterns();
      loadRecentPatterns(7); // Default to 7 days
    } else if (tabValue === 8) {
      // For the Bulk DNS Checks tab (now tabValue 8, not 7), load initial data without starting polling
      logger.log('Loading bulk DNS checks tab data');
      
      // One-time load of stats
      (async () => {
        try {
          const statsResponse = await api.getDnsBulkStats();
          if (statsResponse.success) {
            setBulkStats(statsResponse.data);
          }
        } catch (statsError) {
          logger.error('Error loading bulk stats:', statsError);
          setBulkCheckError('Failed to load bulk DNS check statistics');
        } finally {
          setBulkCheckLoading(false);
        }
      })();
      
      // One-time check for active operations
      (async () => {
        try {
          const opsResponse = await api.getDnsBulkCheckOperations();
          if (opsResponse.success) {
            setBulkCheckOperations(opsResponse.data || []);
            
            // Only look for active operations (running or queued)
            const activeOp = (opsResponse.data || []).find(op => 
              op.status === 'running' || op.status === 'queued'
            );
            
            if (activeOp) {
              logger.log(`Found active operation: ${activeOp.id} (${activeOp.status})`);
              setActiveBulkOperation(activeOp);
              
              // Start polling only if we have an active operation
              startStatusPolling(activeOp.id);
            } else {
              logger.log('No active operations found');
              setActiveBulkOperation(null);
            }
          }
        } catch (opsError) {
          logger.error('Error loading operations:', opsError);
        }
      })();
    }
    
    // Return cleanup function
    return () => {
      logger.log('%c🛑 DNS STATISTICS COMPONENT UNMOUNTED', 'background: #F44336; color: white; font-size: 14px; font-weight: bold; padding: 5px;');
      stopStatusPolling();
    };
  }, [tabValue]);
  
  // Reset tab value for supervisors if they have admin-only tabs selected
  useEffect(() => {
    // If not admin and on an admin-only tab, reset to overview tab
    if (!isAdmin && (tabValue === 7 || tabValue === 8)) {
      setTabValue(0);
    }
  }, [isAdmin, tabValue]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    // For supervisors, we need to make sure they can't access admin-only tabs
    if (!isAdmin && (newValue === 7 || newValue === 8)) {
      return;
    }
    setTabValue(newValue);
  };

  // Handle manual reload
  const handleManualReload = () => {
    loadData();
  };
  
  // Handle starting a bulk check
  const handleStartBulkCheck = () => {
    setStartBulkCheckDialogOpen(true);
  };

  // Prepare data for charts
  const emailProviderChartData = emailProviderStats.map(item => ({
    name: item.provider,
    value: item.count,
    percentage: item.percentage
  }));

  const dmarcPolicyChartData = dmarcPolicyStats.map(item => ({
    name: item.policy,
    value: item.count,
    percentage: item.percentage
  }));
  
  const spfProviderChartData = spfProviderStats.map(item => ({
    name: item.provider,
    value: item.count,
    percentage: item.percentage
  }));

  const dmarcRuaChartData = dmarcRuaStats.map(item => ({
    name: item.provider,
    value: item.count,
    percentage: item.percentage
  }));

  const securityProtocolsChartData = generalStats ? [
    { name: 'SPF', present: generalStats.spf.count, missing: generalStats.total_domains - generalStats.spf.count },
    { name: 'DMARC', present: generalStats.dmarc.count, missing: generalStats.total_domains - generalStats.dmarc.count },
    { name: 'DKIM', present: generalStats.dkim.count, missing: generalStats.total_domains - generalStats.dkim.count }
  ] : [];

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1, bgcolor: 'background.paper', boxShadow: 3 }}>
          <Typography variant="subtitle2">{payload[0].name}</Typography>
          <Typography variant="body2">Count: {payload[0].value}</Typography>
          <Typography variant="body2">Percentage: {payload[0].payload.percentage}%</Typography>
        </Paper>
      );
    }
    return null;
  };

  // Donut Chart component
  const DonutChart = ({ data, colors, dataKey = "value" }) => (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          innerRadius={60}
          outerRadius={100}
          fill="#8884d8"
          paddingAngle={2}
          dataKey={dataKey}
          nameKey="name"
          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
          labelLine={false}
        >
          {data.map((entry, index) => {
            // Use custom color if available
            const color = colors[entry.name] || COLORS[index % COLORS.length];
            return <Cell key={`cell-${index}`} fill={color} />;
          })}
        </Pie>
        <RechartsTooltip content={<CustomTooltip />} />
      </PieChart>
    </ResponsiveContainer>
  );

  // Add a manual test function that can be triggered from the UI
  const testPolling = () => {
    if (activeBulkOperation) {
      logger.log(`Manual test: starting polling for operation ${activeBulkOperation.id}`);
      stopStatusPolling(); // Ensure any existing polling is stopped
      setIsPolling(true); // Set the flag
      startStatusPolling(activeBulkOperation.id, true); // Start with rapid polling
    } else {
      logger.log('Manual test: No active operation to poll');
      showSnackbar('No active operation to poll', 'error');
    }
  };

  // Function to normalize domains that can be reused for both chart and table
  const normalizeDomain = (domain) => {
    // Early exit for domains that should remain as-is
    if (domain === "hornetsecurity.com" || 
        domain === "onlinemailscanner.com" ||
        domain === "solcon.nl") {
      return domain;
    }
    
    // List of top-level domains to prevent over-grouping
    const tlds = ['com', 'net', 'org', 'nl', 'eu', 'de', 'uk', 'co', 'io'];
    
    // Function to check if a domain is just a TLD or too short to be valid
    const isTooGeneric = (domain) => {
      if (!domain || domain.length < 3) return true;
      
      // Check if the domain is just a TLD
      if (tlds.includes(domain)) return true;
      
      // Check if it's a country-specific TLD (co.uk, etc.)
      if (domain.split('.').length === 2 && 
          tlds.includes(domain.split('.')[0]) && 
          tlds.includes(domain.split('.')[1])) {
        return true;
      }
      
      return false;
    };
    
    // Handle specific known providers - exact mappings
    const specificProviders = {
      // spamexperts variants
      'lastmx.spamexperts.net': 'spamexperts.com',
      'fallbackmx.spamexperts.eu': 'spamexperts.com',
      // yourfilter variants
      'primary.yourfilter.nl': 'yourfilter.nl',
      'fallback.yourfilter.nl': 'yourfilter.nl',
      // mtaroutes variants
      'mx1-eu.mtaroutes.com': 'mtaroutes.com',
      'mx2-eu.mtaroutes.com': 'mtaroutes.com',
      'mx3-eu.mtaroutes.com': 'mtaroutes.com',
      'mx4-eu.mtaroutes.com': 'mtaroutes.com',
      // mimecast variants
      'eu-smtp-inbound-1.mimecast.com': 'mimecast.com',
      'eu-smtp-inbound-2.mimecast.com': 'mimecast.com',
      'de-smtp-inbound-1.mimecast.com': 'mimecast.com',
      'de-smtp-inbound-2.mimecast.com': 'mimecast.com',
      // hostnet variants
      'mailfilter.hostnet.nl': 'hostnet.nl',
      // ppe-hosted variants
      'mx1-eu1.ppe-hosted.com': 'ppe-hosted.com',
      'mx2-eu1.ppe-hosted.com': 'ppe-hosted.com',
      'mx1-us1.ppe-hosted.com': 'ppe-hosted.com',
      'mx2-us1.ppe-hosted.com': 'ppe-hosted.com',
      // mailpod13 variants - keep as one.com
      'mx1-hostnet-nl.pub.mailpod13-cph3.one.com': 'one.com',
      'mx2-hostnet-nl.pub.mailpod13-cph3.one.com': 'one.com',
      'mx3-hostnet-nl.pub.mailpod13-cph3.one.com': 'one.com',
      'mx4-hostnet-nl.pub.mailpod13-cph3.one.com': 'one.com'
    };
    
    // Check for direct matches in our specific provider list first
    if (specificProviders[domain]) {
      return specificProviders[domain];
    }
    
    // Special case for known provider domains
    if (domain.includes('spamexperts.')) return 'spamexperts.com';
    if (domain.includes('yourfilter.nl')) return 'yourfilter.nl';
    if (domain.includes('mimecast.com')) return 'mimecast.com';
    if (domain.includes('mtaroutes.com')) return 'mtaroutes.com';
    if (domain.includes('idefender.nl')) return 'idefender.nl';
    if (domain.includes('transip.nl')) return 'transip.nl';
    if (domain.includes('hostnet.nl')) return 'hostnet.nl';
    if (domain.includes('filtermail.eu')) return 'filtermail.eu';
    
    // Special case for ppe-hosted.com
    if (domain.includes('ppe-hosted.com')) return 'ppe-hosted.com';
    
    // Special case for one.com and mailpod
    if (domain.includes('one.com') || domain.includes('mailpod')) return 'one.com';
    
    // Define patterns for matching
    const patterns = [
      // MX patterns
      { pattern: /^mx[0-9]+\.(.*)/i, groupIndex: 1 },
      // MX with hyphen (mx1-eu, etc.)
      { pattern: /^mx[0-9]+-.*\.(.*)/i, groupIndex: 1 },
      // Primary/fallback patterns
      { pattern: /^primary\.(.*)/i, groupIndex: 1 },
      { pattern: /^fallback\.(.*)/i, groupIndex: 1 },
      { pattern: /^backup\.(.*)/i, groupIndex: 1 },
      { pattern: /^lastmx\.(.*)/i, groupIndex: 1 },
      // Mail/SMTP patterns
      { pattern: /^mail\.(.*)/i, groupIndex: 1 },
      { pattern: /^smtp[0-9]*\.(.*)/i, groupIndex: 1 },
      { pattern: /^exchange[0-9]*\.(.*)/i, groupIndex: 1 },
      // Filter patterns
      { pattern: /^filter[0-9]*\.(.*)/i, groupIndex: 1 },
      { pattern: /^mailfilter\.(.*)/i, groupIndex: 1 },
      // Relay pattern
      { pattern: /^relay\.(.*)/i, groupIndex: 1 },
      // Regional pattern (eu-something.domain.com)
      { pattern: /^(eu|de|us|uk|fr|nl|au|ca|ap|as)-(.*)\.(.*)/i, groupIndex: 3 },
      // Inbound/outbound patterns
      { pattern: /^.*-inbound-[0-9]+\.(.*)/i, groupIndex: 1 },
      { pattern: /^.*-outbound-[0-9]+\.(.*)/i, groupIndex: 1 }
    ];
    
    // Try all patterns in sequence
    for (const { pattern, groupIndex } of patterns) {
      const match = domain.match(pattern);
      if (match && match[groupIndex]) {
        const candidate = match[groupIndex];
        // Prevent returning just a TLD
        if (!isTooGeneric(candidate)) {
          return candidate;
        }
      }
    }
    
    // If no pattern matched, check for common subdomain prefixes
    const domainParts = domain.split('.');
    if (domainParts.length > 2) {
      const commonPrefixes = ['mail', 'smtp', 'mx', 'exchange', 'mailserver', 'email', 'post', 'filter', 'scan'];
      if (commonPrefixes.includes(domainParts[0].toLowerCase())) {
        const candidate = domainParts.slice(1).join('.');
        // Check that we're not just returning a TLD
        if (!isTooGeneric(candidate)) {
          return candidate;
        }
      }
    }
    
    // Default: return original domain
    return domain;
  };

  // Grouped MX provider data for table display
  const groupedMxProviderTableData = React.useMemo(() => {
    if (!mxProviderStats || mxProviderStats.length === 0) return [];
    
    // Create a map to group by domain
    const domainGroups = {};
    
    // Process each MX provider
    mxProviderStats.forEach(item => {
      const provider = item.provider;
      
      // Get normalized domain for grouping
      const baseDomain = normalizeDomain(provider);
      
      // Add to or create group - either way we track original domains
      if (!domainGroups[baseDomain]) {
        domainGroups[baseDomain] = {
          provider: baseDomain,
          count: item.count,
          percentage: item.percentage,
          domains: [provider]
        };
      } else {
        domainGroups[baseDomain].count += item.count;
        domainGroups[baseDomain].percentage += item.percentage;
        
        // Only add domain to list if not already present
        if (!domainGroups[baseDomain].domains.includes(provider)) {
          domainGroups[baseDomain].domains.push(provider);
        }
      }
    });
    
    // Convert groups to array and sort by count
    return Object.values(domainGroups)
      .sort((a, b) => b.count - a.count)
      .map(group => ({
        ...group,
        percentage: Math.round(group.percentage * 10) / 10 // Round to 1 decimal place
      }));
  }, [mxProviderStats]);

  // MX Provider Chart Data - Modified to exclude self-hosted and group by domain
  // Convert to a more direct approach without useMemo
  const mxProviderChartData = (() => {
    // First, exclude "Self-hosted" entries
    const filteredStats = mxProviderStats.filter(item => item.provider !== "Self-hosted");
    
    // Create a map to group by base domain
    const domainGroups = {};
    
    // Process each MX provider
    filteredStats.forEach(item => {
      const provider = item.provider;
      
      // Get normalized domain for grouping
      const baseDomain = normalizeDomain(provider);
      
      // Add to or create group
      if (!domainGroups[baseDomain]) {
        domainGroups[baseDomain] = {
          name: baseDomain,
          value: item.count,
          percentage: item.percentage,
          domains: [provider]
        };
      } else {
        domainGroups[baseDomain].value += item.count;
        domainGroups[baseDomain].percentage += item.percentage;
        
        // Only add domain to list if not already present
        if (!domainGroups[baseDomain].domains.includes(provider)) {
          domainGroups[baseDomain].domains.push(provider);
        }
      }
    });
    
    // Convert groups to array and sort by count
    const result = Object.values(domainGroups)
      .sort((a, b) => b.value - a.value)
      .map(group => ({
        ...group,
        percentage: Math.round(group.percentage * 10) / 10 // Round to 1 decimal place
      }));
      
    return result;
  })();

  // Verification services chart data
  const verificationServicesChartData = verificationStats?.services
    ? verificationStats.services.map(item => ({
        name: item.provider,
        value: item.count,
        percentage: item.percentage
      }))
    : [];

  // If not admin or supervisor, show access denied
  if (!isAdmin && !isSupervisor) {
    return (
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
        <Alert severity="error">
          Access Denied. This page is only available for administrators and supervisors.
        </Alert>
      </Paper>
    );
  }

  // Functions for pattern management tab
  const loadCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await api.getDnsVerificationCategories();
      if (response.success) {
        setCategoriesList(response.categories || []);
      } else {
        showSnackbar(response.message || 'Failed to load categories', 'error');
      }
    } catch (error) {
      logger.error('Error loading categories:', error);
      showSnackbar('Error loading categories', 'error');
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadPatterns = async () => {
    setLoadingPatterns(true);
    try {
      const response = await api.getDnsVerificationPatterns();
      if (response.success) {
        setPatternsList(response.patterns || []);
      } else {
        showSnackbar(response.message || 'Failed to load patterns', 'error');
      }
    } catch (error) {
      logger.error('Error loading patterns:', error);
      showSnackbar('Error loading patterns', 'error');
    } finally {
      setLoadingPatterns(false);
    }
  };

  const loadRecentPatterns = async (days = 7) => {
    setLoadingPatterns(true);
    try {
      const response = await api.getRecentlyAddedDnsVerificationPatterns(days);
      if (response.success) {
        setRecentPatterns(response.patterns || []);
      } else {
        showSnackbar(response.message || 'Failed to load recent patterns', 'error');
      }
    } catch (error) {
      logger.error('Error loading recent patterns:', error);
      showSnackbar('Error loading recent patterns', 'error');
    } finally {
      setLoadingPatterns(false);
    }
  };

  // Add pattern and category save handlers
  const handleSavePattern = async () => {
    if (!currentPattern.pattern || !currentPattern.service || !currentPattern.category_id) {
      setPatternFormError('Pattern, service, and category are required');
      return;
    }

    try {
      let response;
      if (patternDialogMode === 'add') {
        response = await api.addDnsVerificationPattern(currentPattern);
      } else {
        response = await api.updateDnsVerificationPattern(currentPattern.id, currentPattern);
      }

      if (response.success) {
        showSnackbar(
          patternDialogMode === 'add' 
            ? 'Pattern added successfully' 
            : 'Pattern updated successfully', 
          'success'
        );
        setPatternDialogOpen(false);
        // Reload patterns
        loadPatterns();
        loadRecentPatterns(recentDays);
      } else {
        setPatternFormError(response.message || 'Failed to save pattern');
      }
    } catch (error) {
      logger.error('Error saving pattern:', error);
      setPatternFormError('Error saving pattern: ' + (error.message || 'Unknown error'));
    }
  };

  const handleSaveCategory = async () => {
    if (!currentCategory.name) {
      setCategoryFormError('Category name is required');
      return;
    }

    try {
      let response;
      if (categoryDialogMode === 'add') {
        response = await api.addDnsVerificationCategory(currentCategory);
      } else {
        response = await api.updateDnsVerificationCategory(currentCategory.id, currentCategory);
      }

      if (response.success) {
        showSnackbar(
          categoryDialogMode === 'add' 
            ? 'Category added successfully' 
            : 'Category updated successfully', 
          'success'
        );
        setCategoryDialogOpen(false);
        // Reload categories
        loadCategories();
      } else {
        setCategoryFormError(response.message || 'Failed to save category');
      }
    } catch (error) {
      logger.error('Error saving category:', error);
      setCategoryFormError('Error saving category: ' + (error.message || 'Unknown error'));
    }
  };

  // Add delete handler
  const handleConfirmDelete = async () => {
    try {
      let response;
      if (confirmDeleteDialog.type === 'pattern') {
        response = await api.deleteDnsVerificationPattern(confirmDeleteDialog.id);
      } else {
        response = await api.deleteDnsVerificationCategory(confirmDeleteDialog.id);
      }

      if (response.success) {
        showSnackbar(
          `${confirmDeleteDialog.type === 'pattern' ? 'Pattern' : 'Category'} deleted successfully`, 
          'success'
        );
        // If we deleted the selected category, clear the selection
        if (confirmDeleteDialog.type === 'category' && 
            selectedCategory?.id === confirmDeleteDialog.id) {
          setSelectedCategory(null);
        }
        // Reload data
        if (confirmDeleteDialog.type === 'pattern') {
          loadPatterns();
          loadRecentPatterns(recentDays);
        } else {
          loadCategories();
          loadPatterns(); // Need to reload patterns as some may be deleted
        }
      } else {
        showSnackbar(
          `Failed to delete ${confirmDeleteDialog.type}: ${response.message || 'Unknown error'}`,
          'error'
        );
      }
    } catch (error) {
      logger.error(`Error deleting ${confirmDeleteDialog.type}:`, error);
      showSnackbar(
        `Error deleting ${confirmDeleteDialog.type}: ${error.message || 'Unknown error'}`,
        'error'
      );
    } finally {
      setConfirmDeleteDialog({...confirmDeleteDialog, open: false});
    }
    };

  // This useEffect was moved to be with the other hooks at the top level

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          DNS Statistics
        </Typography>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />} 
          onClick={handleManualReload}
          disabled={loading}
        >
          Reload Data
        </Button>
      </Box>

      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading indicator */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Tabs */}
      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Overview" />
        <Tab label="Email Providers" />
        <Tab label="DMARC Policies" />
        <Tab label="SPF Providers" />
        <Tab label="DMARC RUA" />
        <Tab label="MX Providers" />
        <Tab label="Verification Services" />
        {isAdmin && <Tab label="Pattern Management" />}
        {isAdmin && <Tab label="Bulk DNS Checks" />}
      </Tabs>

      {/* Overview Tab */}
      {tabValue === 0 && generalStats && !loading && (
        <Grid container spacing={3}>
          {/* Total Domains Card */}
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  {generalStats.total_domains}
                </Typography>
                <Typography variant="body2">Total Domains</Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Security Protocols Card */}
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'success.main', color: 'success.contrastText' }}>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  {generalStats.all_three.count}
                </Typography>
                <Typography variant="body2">
                  Complete Security
                  <Tooltip title="Domains with SPF, DMARC, and DKIM configured">
                    <Typography variant="caption" display="block">
                      {generalStats.all_three.percentage}% of domains
                    </Typography>
                  </Tooltip>
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={generalStats.all_three.percentage} 
                  sx={{ mt: 1, bgcolor: 'success.light' }} 
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Microsoft 365 Card */}
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#0078d4', color: 'white' }}>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  {generalStats.microsoft.count}
                </Typography>
                <Typography variant="body2">
                  Microsoft Verified
                  <Typography variant="caption" display="block">
                    {generalStats.microsoft.percentage}% of domains
                  </Typography>
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={generalStats.microsoft.percentage} 
                  sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.3)' }} 
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Azure Card */}
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#00a2ed', color: 'white' }}>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  {generalStats.azure.count}
                </Typography>
                <Typography variant="body2">
                  Azure Enrolled
                  <Typography variant="caption" display="block">
                    {generalStats.azure.percentage}% of domains
                  </Typography>
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={generalStats.azure.percentage} 
                  sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.3)' }} 
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Security Protocols Chart */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <SecurityIcon sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
                  Security Protocols
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={securityProtocolsChartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="present" name="Present" fill="#4caf50" stackId="stack" />
                    <Bar dataKey="missing" name="Missing" fill="#f44336" stackId="stack" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Security Details Table */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Security Details
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Security Protocol</TableCell>
                        <TableCell align="right">Count</TableCell>
                        <TableCell align="right">Percentage</TableCell>
                        <TableCell align="center">Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>SPF</TableCell>
                        <TableCell align="right">{generalStats.spf.count}</TableCell>
                        <TableCell align="right">{generalStats.spf.percentage}%</TableCell>
                        <TableCell align="center">
                          <Chip 
                            size="small"
                            icon={generalStats.spf.percentage > 80 ? <CheckCircleIcon /> : <CancelIcon />}
                            label={generalStats.spf.percentage > 80 ? "Good" : "Needs Improvement"}
                            color={generalStats.spf.percentage > 80 ? "success" : "warning"}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>DMARC</TableCell>
                        <TableCell align="right">{generalStats.dmarc.count}</TableCell>
                        <TableCell align="right">{generalStats.dmarc.percentage}%</TableCell>
                        <TableCell align="center">
                          <Chip 
                            size="small"
                            icon={generalStats.dmarc.percentage > 60 ? <CheckCircleIcon /> : <CancelIcon />}
                            label={generalStats.dmarc.percentage > 60 ? "Good" : "Needs Improvement"}
                            color={generalStats.dmarc.percentage > 60 ? "success" : "warning"}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>DKIM</TableCell>
                        <TableCell align="right">{generalStats.dkim.count}</TableCell>
                        <TableCell align="right">{generalStats.dkim.percentage}%</TableCell>
                        <TableCell align="center">
                          <Chip 
                            size="small"
                            icon={generalStats.dkim.percentage > 45 ? <CheckCircleIcon /> : <CancelIcon />}
                            label={generalStats.dkim.percentage > 45 ? "Good" : "Needs Improvement"}
                            color={generalStats.dkim.percentage > 45 ? "success" : "warning"}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>All Three</TableCell>
                        <TableCell align="right">{generalStats.all_three.count}</TableCell>
                        <TableCell align="right">{generalStats.all_three.percentage}%</TableCell>
                        <TableCell align="center">
                          <Chip 
                            size="small"
                            icon={generalStats.all_three.percentage > 35 ? <CheckCircleIcon /> : <CancelIcon />}
                            label={generalStats.all_three.percentage > 35 ? "Good" : "Needs Improvement"}
                            color={generalStats.all_three.percentage > 35 ? "success" : "warning"}
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Microsoft Integration Stats */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Microsoft Integration
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h5">{generalStats.microsoft.count}</Typography>
                      <Typography variant="body2" color="text.secondary">Microsoft Verification</Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={generalStats.microsoft.percentage} 
                        sx={{ mt: 1, height: 10, borderRadius: 5 }} 
                      />
                      <Typography variant="caption" color="text.secondary">
                        {generalStats.microsoft.percentage}% of domains
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h5">{generalStats.azure.count}</Typography>
                      <Typography variant="body2" color="text.secondary">Azure Enrollment</Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={generalStats.azure.percentage} 
                        sx={{ mt: 1, height: 10, borderRadius: 5 }} 
                      />
                      <Typography variant="caption" color="text.secondary">
                        {generalStats.azure.percentage}% of domains
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h5">{generalStats.enterprise.count}</Typography>
                      <Typography variant="body2" color="text.secondary">Enterprise Enrollment</Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={generalStats.enterprise.percentage} 
                        sx={{ mt: 1, height: 10, borderRadius: 5 }} 
                      />
                      <Typography variant="caption" color="text.secondary">
                        {generalStats.enterprise.percentage}% of domains
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Email Providers Tab */}
      {tabValue === 1 && !loading && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <EmailIcon sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
                  Email Provider Distribution
                </Typography>
                <DonutChart 
                  data={emailProviderChartData} 
                  colors={EMAIL_PROVIDER_COLORS} 
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Email Provider Details
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Provider</TableCell>
                        <TableCell align="right">Domains</TableCell>
                        <TableCell align="right">Percentage</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {emailProviderStats.map((provider) => (
                        <TableRow key={provider.provider}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box
                                sx={{
                                  width: 16,
                                  height: 16,
                                  borderRadius: '50%',
                                  bgcolor: EMAIL_PROVIDER_COLORS[provider.provider] || COLORS[0],
                                  mr: 1
                                }}
                              />
                              {provider.provider}
                            </Box>
                          </TableCell>
                          <TableCell align="right">{provider.count}</TableCell>
                          <TableCell align="right">{provider.percentage}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Email Provider Details
                  </Typography>
                  <Typography variant="body2">
                    <strong>M365:</strong> Microsoft 365 (Direct)
                  </Typography>
                  <Typography variant="body2">
                    <strong>M365_Filtered:</strong> Microsoft 365 with third-party email filtering
                  </Typography>
                  <Typography variant="body2">
                    <strong>Google:</strong> Google Workspace
                  </Typography>
                  <Typography variant="body2">
                    <strong>Other:</strong> Other email providers or self-hosted
                  </Typography>
                  <Typography variant="body2">
                    <strong>Unknown:</strong> Provider couldn't be determined
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* DMARC Policies Tab */}
      {tabValue === 2 && !loading && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <SecurityIcon sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
                  DMARC Policy Distribution
                </Typography>
                <DonutChart 
                  data={dmarcPolicyChartData} 
                  colors={DMARC_POLICY_COLORS} 
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  DMARC Policy Details
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Policy</TableCell>
                        <TableCell align="right">Domains</TableCell>
                        <TableCell align="right">Percentage</TableCell>
                        <TableCell align="center">Security Level</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dmarcPolicyStats.map((policy) => (
                        <TableRow key={policy.policy}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box
                                sx={{
                                  width: 16,
                                  height: 16,
                                  borderRadius: '50%',
                                  bgcolor: DMARC_POLICY_COLORS[policy.policy] || COLORS[0],
                                  mr: 1
                                }}
                              />
                              {policy.policy}
                            </Box>
                          </TableCell>
                          <TableCell align="right">{policy.count}</TableCell>
                          <TableCell align="right">{policy.percentage}%</TableCell>
                          <TableCell align="center">
                            {policy.policy === 'reject' && (
                              <Chip size="small" label="Highest" color="success" />
                            )}
                            {policy.policy === 'quarantine' && (
                              <Chip size="small" label="Medium" color="warning" />
                            )}
                            {policy.policy === 'none' && (
                              <Chip size="small" label="Monitoring Only" color="error" />
                            )}
                            {policy.policy === 'No DMARC' && (
                              <Chip size="small" label="None" color="default" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Understanding DMARC Policies
                  </Typography>
                  <Typography variant="body2">
                    <strong>reject:</strong> Highest security. Email that fails DMARC is rejected by the receiving server.
                  </Typography>
                  <Typography variant="body2">
                    <strong>quarantine:</strong> Medium security. Email that fails DMARC is quarantined (typically sent to spam folder).
                  </Typography>
                  <Typography variant="body2">
                    <strong>none:</strong> Monitoring only. No action is taken on emails that fail DMARC.
                  </Typography>
                  <Typography variant="body2">
                    <strong>No DMARC:</strong> No DMARC record exists for the domain.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      {/* SPF Providers Tab */}
      {tabValue === 3 && !loading && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <SecurityIcon sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
                  Top 20 SPF Provider Distribution
                </Typography>
                <Typography variant="caption" color="text.secondary" paragraph>
                  Showing top 20 third-party services referenced in SPF records (excluding primary email providers)
                </Typography>
                <ResponsiveContainer width="100%" height={650}>
                  <BarChart
                    data={spfProviderChartData.slice(0, 20)}
                    layout="vertical"
                    margin={{ top: 5, right: 50, left: 5, bottom: 20 }}
                    barSize={28}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      type="number" 
                      label={{ 
                        value: 'Number of domains', 
                        position: 'insideBottomRight', 
                        offset: -10 
                      }}
                    />
                    <YAxis 
                      hide={true}
                      type="category" 
                      dataKey="name" 
                      width={0}
                      tick={false}
                    />
                    <RechartsTooltip 
                      formatter={(value, name, props) => [
                        `${value} domains (${props.payload.percentage}%)`,
                        props.payload.name
                      ]}
                      cursor={{ fill: 'rgba(0, 0, 0, 0.1)' }}
                    />
                    <Bar 
                      dataKey="value" 
                      name="Count"
                      fill="#8884d8"
                      label={(props) => {
                        const { x, y, width, height, value, name } = props;
                        return (
                          <>
                            <text 
                              x={x + 10} 
                              y={y + height/2 + 5}
                              textAnchor="start"
                              fill="#FFFFFF" 
                              fontSize={12}
                              fontWeight="bold"
                            >
                              {name}
                            </text>
                            <text 
                              x={x + width - 10} 
                              y={y + height/2 + 5} 
                              textAnchor="end" 
                              fill="#FFFFFF" 
                              fontSize={12}
                              fontWeight="bold"
                            >
                              {value}
                            </text>
                          </>
                        );
                      }}
                    >
                      {spfProviderChartData.slice(0, 20).map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={SPF_PROVIDER_COLORS[entry.name] || COLORS[index % COLORS.length]} 
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  SPF Provider Details
                </Typography>
                <Typography variant="caption" color="text.secondary" paragraph>
                  All third-party services included in SPF records via 'include:' mechanism
                </Typography>
                
                {/* Search Box */}
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Search providers"
                    InputProps={{
                      startAdornment: (
                        <Box component="span" sx={{ color: 'action.active', mr: 1 }}>
                          <SearchIcon fontSize="small" />
                        </Box>
                      ),
                    }}
                    placeholder="Type to filter by domain name..."
                    onChange={(e) => {
                      // Filter the spfProviderStats based on search term
                      const searchTerm = e.target.value.toLowerCase();
                      if (!searchTerm) {
                        // If search is empty, show all providers
                        setFilteredSpfProviders(spfProviderStats);
                      } else {
                        // Otherwise, filter by domain name
                        const filtered = spfProviderStats.filter(provider => 
                          provider.provider.toLowerCase().includes(searchTerm)
                        );
                        setFilteredSpfProviders(filtered);
                      }
                    }}
                  />
                </Box>
                
                <TableContainer sx={{ maxHeight: 550 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Provider Domain</TableCell>
                        <TableCell align="right">Domains</TableCell>
                        <TableCell align="right">Percentage</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(filteredSpfProviders || spfProviderStats).map((provider) => (
                        <TableRow key={provider.provider}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box
                                sx={{
                                  width: 16,
                                  height: 16,
                                  borderRadius: '50%',
                                  bgcolor: SPF_PROVIDER_COLORS[provider.provider] || COLORS[0],
                                  mr: 1
                                }}
                              />
                              {provider.provider}
                            </Box>
                          </TableCell>
                          <TableCell align="right">{provider.count}</TableCell>
                          <TableCell align="right">{provider.percentage}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Note: Primary email providers like Microsoft (outlook.com) and Google (google.com) 
                    are excluded from this chart.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      {/* DMARC RUA Tab */}
      {tabValue === 4 && !loading && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <SecurityIcon sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
                  Top 20 DMARC RUA Provider Distribution
                </Typography>
                <Typography variant="caption" color="text.secondary" paragraph>
                  Showing top 20 DMARC RUA recipient services (domains extracted from RUA mailto: addresses, excluding "Self")
                </Typography>
                <ResponsiveContainer width="100%" height={650}>
                  <BarChart
                    data={dmarcRuaChartData.filter(item => item.name !== "Self").slice(0, 20)}
                    layout="vertical"
                    margin={{ top: 5, right: 50, left: 5, bottom: 20 }}
                    barSize={28}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      type="number" 
                      label={{ 
                        value: 'Number of domains', 
                        position: 'insideBottomRight', 
                        offset: -10 
                      }}
                    />
                    <YAxis 
                      hide={true}
                      type="category" 
                      dataKey="name" 
                      width={0}
                      tick={false}
                    />
                    <RechartsTooltip 
                      formatter={(value, name, props) => [
                        `${value} domains (${props.payload.percentage}%)`,
                        props.payload.name
                      ]}
                      cursor={{ fill: 'rgba(0, 0, 0, 0.1)' }}
                    />
                    <Bar 
                      dataKey="value" 
                      name="Count"
                      fill="#8884d8"
                      label={(props) => {
                        const { x, y, width, height, value, name } = props;
                        return (
                          <>
                            <text 
                              x={x + 10} 
                              y={y + height/2 + 5}
                              textAnchor="start"
                              fill="#FFFFFF" 
                              fontSize={12}
                              fontWeight="bold"
                            >
                              {name}
                            </text>
                            <text 
                              x={x + width - 10} 
                              y={y + height/2 + 5} 
                              textAnchor="end" 
                              fill="#FFFFFF" 
                              fontSize={12}
                              fontWeight="bold"
                            >
                              {value}
                            </text>
                          </>
                        );
                      }}
                    >
                      {dmarcRuaChartData.filter(item => item.name !== "Self").slice(0, 20).map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={RUA_PROVIDER_COLORS[entry.name] || COLORS[index % COLORS.length]} 
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  DMARC RUA Provider Details
                </Typography>
                <Typography variant="caption" color="text.secondary" paragraph>
                  All domains extracted from DMARC RUA fields that receive reports
                </Typography>
                
                {/* Search Box */}
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Search providers"
                    InputProps={{
                      startAdornment: (
                        <Box component="span" sx={{ color: 'action.active', mr: 1 }}>
                          <SearchIcon fontSize="small" />
                        </Box>
                      ),
                    }}
                    placeholder="Type to filter by domain name..."
                    onChange={(e) => {
                      // Filter the dmarcRuaStats based on search term
                      const searchTerm = e.target.value.toLowerCase();
                      if (!searchTerm) {
                        // If search is empty, show all providers
                        setFilteredDmarcRua(dmarcRuaStats);
                      } else {
                        // Otherwise, filter by domain name
                        const filtered = dmarcRuaStats.filter(provider => 
                          provider.provider.toLowerCase().includes(searchTerm)
                        );
                        setFilteredDmarcRua(filtered);
                      }
                    }}
                  />
                </Box>
                
                <TableContainer sx={{ maxHeight: 550 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Provider Domain</TableCell>
                        <TableCell align="right">Domains</TableCell>
                        <TableCell align="right">Percentage</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(filteredDmarcRua || dmarcRuaStats).map((provider) => (
                        <TableRow key={provider.provider}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box
                                sx={{
                                  width: 16,
                                  height: 16,
                                  borderRadius: '50%',
                                  bgcolor: RUA_PROVIDER_COLORS[provider.provider] || COLORS[0],
                                  mr: 1
                                }}
                              />
                              {provider.provider}
                            </Box>
                          </TableCell>
                          <TableCell align="right">{provider.count}</TableCell>
                          <TableCell align="right">{provider.percentage}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Note: "Self" indicates that the RUA address uses the domain's own mail server.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      {/* MX Providers Tab */}
      {tabValue === 5 && !loading && (
        <Grid container spacing={3}>
          {/* MX Provider Details */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
              <DnsIcon sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
              MX Record Details
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              This shows the distribution of MX records from domains classified as "Other" in the email provider distribution.
              MX records are grouped by domain name (e.g., mx1.domain.com and mx2.domain.com are shown as domain.com).
              Self-hosted mail servers are excluded from the chart but shown in the table below.
            </Alert>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Top 20 MX Provider Distribution
                </Typography>
                <Typography variant="caption" color="text.secondary" paragraph>
                  Showing top 20 third-party MX record domains (excluding primary email providers like Microsoft and Google, and self-hosted servers)
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={mxProviderChartData.slice(0, 20)}
                    layout="vertical"
                    margin={{ top: 5, right: 50, left: 5, bottom: 20 }}
                    barSize={28}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      type="number" 
                      label={{ 
                        value: 'Number of domains', 
                        position: 'insideBottomRight', 
                        offset: -10 
                      }}
                    />
                    <YAxis 
                      hide={true}
                      type="category" 
                      dataKey="name" 
                      width={0}
                      tick={false}
                    />
                    <RechartsTooltip 
                      content={({ active, payload }) => {
                        if (!active || !payload || payload.length === 0) return null;
                        
                        const data = payload[0].payload;
                        logger.log("MX Chart tooltip data:", data);
                        
                        return (
                          <Paper sx={{ p: 2, bgcolor: 'background.paper', boxShadow: 3, maxWidth: 300 }}>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                              {data.name}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2" color="text.secondary">Count:</Typography>
                              <Typography variant="body2" fontWeight="bold">{data.value}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2" color="text.secondary">Percentage:</Typography>
                              <Typography variant="body2" fontWeight="bold">{data.percentage}%</Typography>
                            </Box>
                            
                            {data.domains && data.domains.length > 1 && (
                              <>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="subtitle2" gutterBottom>
                                  MX Records in this group ({data.domains.length}):
                                </Typography>
                                <Box sx={{ ml: 1, mt: 1 }}>
                                  {data.domains.map((domain, idx) => (
                                    <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                                      {idx+1}. {domain}
                                    </Typography>
                                  ))}
                                </Box>
                              </>
                            )}
                          </Paper>
                        );
                      }}
                      cursor={{ fill: 'rgba(0, 0, 0, 0.1)' }}
                    />
                    <Bar 
                      dataKey="value" 
                      name="Count"
                      fill="#8884d8"
                      label={(props) => {
                        const { x, y, width, height, value, name } = props;
                        return (
                          <>
                            <text 
                              x={x + 10} 
                              y={y + height/2 + 5}
                              textAnchor="start"
                              fill="#FFFFFF" 
                              fontSize={12}
                              fontWeight="bold"
                            >
                              {name}
                            </text>
                            <text 
                              x={x + width - 10} 
                              y={y + height/2 + 5} 
                              textAnchor="end" 
                              fill="#FFFFFF" 
                              fontSize={12}
                              fontWeight="bold"
                            >
                              {value}
                            </text>
                          </>
                        );
                      }}
                    >
                      {mxProviderChartData.slice(0, 20).map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={MX_PROVIDER_COLORS[entry.name] || COLORS[index % COLORS.length]} 
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  MX Provider Details
                </Typography>
                
                {/* Search Box with Group by Domain toggle */}
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <TextField
                    size="small"
                    variant="outlined"
                    label="Search MX providers"
                    InputProps={{
                      startAdornment: (
                        <Box component="span" sx={{ color: 'action.active', mr: 1 }}>
                          <SearchIcon fontSize="small" />
                        </Box>
                      ),
                    }}
                    placeholder="Type to filter by domain name..."
                    onChange={(e) => {
                      // Filter the MX providers based on search term
                      const searchTerm = e.target.value.toLowerCase();
                      if (!searchTerm) {
                        // If search is empty, show all providers
                        setFilteredMxProviders(null);
                      } else {
                        // Use appropriate data source based on grouping setting
                        const dataSource = groupMxProviders ? groupedMxProviderTableData : mxProviderStats;
                        
                        // Filter by provider name or any of the domains in the group
                        const filtered = dataSource.filter(provider => {
                          if (provider.provider.toLowerCase().includes(searchTerm)) {
                            return true;
                          }
                          
                          // Also search in domains array if it exists
                          if (provider.domains && provider.domains.length > 0) {
                            return provider.domains.some(domain => 
                              domain.toLowerCase().includes(searchTerm)
                            );
                          }
                          
                          return false;
                        });
                        
                        setFilteredMxProviders(filtered);
                      }
                    }}
                    sx={{ flexGrow: 1, mr: 2 }}
                  />
                  
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ mr: 1 }}>Group by Domain:</Typography>
                    <Chip 
                      label={groupMxProviders ? "On" : "Off"}
                      color={groupMxProviders ? "primary" : "default"}
                      onClick={() => setGroupMxProviders(!groupMxProviders)}
                      clickable
                    />
                  </Box>
                </Box>
                
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Provider Domain</TableCell>
                        <TableCell align="right">Domains</TableCell>
                        <TableCell align="right">Percentage</TableCell>
                        {groupMxProviders && <TableCell>Includes</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* Use either filtered data or appropriate source based on grouping setting */}
                      {(filteredMxProviders || (groupMxProviders ? groupedMxProviderTableData : mxProviderStats)).map((provider) => (
                        <TableRow key={provider.provider}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box
                                sx={{
                                  width: 16,
                                  height: 16,
                                  borderRadius: '50%',
                                  bgcolor: MX_PROVIDER_COLORS[provider.provider] || COLORS[provider.provider.length % COLORS.length],
                                  mr: 1
                                }}
                              />
                              {provider.provider}
                            </Box>
                          </TableCell>
                          <TableCell align="right">{provider.count}</TableCell>
                          <TableCell align="right">{provider.percentage}%</TableCell>
                          {groupMxProviders && provider.domains && provider.domains.length > 1 && (
                            <TableCell>
                              <Tooltip
                                title={
                                  <Box sx={{ p: 1 }}>
                                    {provider.domains.map((domain, idx) => (
                                      <Typography key={idx} variant="body2">
                                        {domain}
                                      </Typography>
                                    ))}
                                  </Box>
                                }
                              >
                                <Chip 
                                  size="small" 
                                  label={`${provider.domains.length} MX records`} 
                                  color="info"
                                  variant="outlined"
                                />
                              </Tooltip>
                            </TableCell>
                          )}
                          {groupMxProviders && (!provider.domains || provider.domains.length <= 1) && (
                            <TableCell>-</TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Note: Primary email providers like Microsoft (outlook.com) and Google (google.com) 
                    are excluded from this chart as they are captured in the Email Provider distribution above.
                    "Self-hosted" indicates domains with mail servers on their own domains (e.g., mail.example.com for example.com).
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      {/* Verification Services Tab */}
      {tabValue === 6 && !loading && (
        <Grid container spacing={3}>
          {/* Verification Services Overview */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
              <VerifiedUserIcon sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
              Domain Verification Services
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              This shows the distribution of domain verification TXT records that organizations use to prove domain ownership for various services.
              Examples include Google Workspace, Microsoft 365, OpenAI, Slack, and more.
            </Alert>
          </Grid>

          {/* Verification Stats Card */}
          {verificationStats && (
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Card sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                    <CardContent>
                      <Typography variant="h5" gutterBottom>
                        {verificationStats.total_domains}
                      </Typography>
                      <Typography variant="body2">Total Domains</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Card sx={{ bgcolor: 'success.main', color: 'success.contrastText' }}>
                    <CardContent>
                      <Typography variant="h5" gutterBottom>
                        {verificationStats.domains_with_verifications}
                      </Typography>
                      <Typography variant="body2">
                        Domains with Verifications
                        <Typography variant="caption" display="block">
                          {verificationStats.verification_percentage}% of domains
                        </Typography>
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={verificationStats.verification_percentage} 
                        sx={{ mt: 1, bgcolor: 'success.light' }} 
                      />
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Card sx={{ bgcolor: 'info.main', color: 'info.contrastText' }}>
                    <CardContent>
                      <Typography variant="h5" gutterBottom>
                        {verificationStats.services?.length || 0}
                      </Typography>
                      <Typography variant="body2">
                        Unique Services Detected
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          )}

          {/* Verification Services Chart */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <VerifiedUserIcon sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
                  Top 20 Domain Verification Services
                </Typography>
                <Typography variant="caption" color="text.secondary" paragraph>
                  Showing the top 20 services that domains have verification records for
                </Typography>
                {verificationServicesChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={500}>
                    <BarChart
                      data={verificationServicesChartData.slice(0, 20)}
                      layout="vertical"
                      margin={{ top: 5, right: 50, left: 5, bottom: 20 }}
                      barSize={28}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        type="number" 
                        label={{ 
                          value: 'Number of domains', 
                          position: 'insideBottomRight', 
                          offset: -10 
                        }}
                      />
                      <YAxis 
                        hide={true}
                        type="category" 
                        dataKey="name" 
                        width={0}
                        tick={false}
                      />
                      <RechartsTooltip 
                        formatter={(value, name, props) => [
                          `${value} domains (${props.payload.percentage}%)`,
                          props.payload.name
                        ]}
                        cursor={{ fill: 'rgba(0, 0, 0, 0.1)' }}
                      />
                      <Bar 
                        dataKey="value" 
                        name="Count"
                        fill="#8884d8"
                        label={(props) => {
                          const { x, y, width, height, value, name } = props;
                          return (
                            <>
                              <text 
                                x={x + 10} 
                                y={y + height/2 + 5}
                                textAnchor="start"
                                fill="#FFFFFF" 
                                fontSize={12}
                                fontWeight="bold"
                              >
                                {name}
                              </text>
                              <text 
                                x={x + width - 10} 
                                y={y + height/2 + 5} 
                                textAnchor="end" 
                                fill="#FFFFFF" 
                                fontSize={12}
                                fontWeight="bold"
                              >
                                {value}
                              </text>
                            </>
                          );
                        }}
                      >
                        {verificationServicesChartData.slice(0, 20).map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={VERIFICATION_SERVICE_COLORS[entry.name] || COLORS[index % COLORS.length]} 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Alert severity="info">No domain verification services found in the database. Try running a DNS check for some domains first.</Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Verification Services Table */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Verification Services Details
                </Typography>
                <Typography variant="caption" color="text.secondary" paragraph>
                  Full list of domain verification services detected in TXT records
                </Typography>
                
                {/* Search Box */}
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Search services"
                    InputProps={{
                      startAdornment: (
                        <Box component="span" sx={{ color: 'action.active', mr: 1 }}>
                          <SearchIcon fontSize="small" />
                        </Box>
                      ),
                    }}
                    placeholder="Type to filter by service name..."
                    onChange={(e) => {
                      // Filter the services based on search term
                      const searchTerm = e.target.value.toLowerCase();
                      if (!searchTerm) {
                        // If search is empty, show all services
                        setFilteredVerificationServices(null);
                      } else {
                        // Otherwise, filter by service name
                        const filtered = verificationStats?.services?.filter(service => 
                          service.provider.toLowerCase().includes(searchTerm)
                        ) || [];
                        setFilteredVerificationServices(filtered);
                      }
                    }}
                  />
                </Box>
                
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Service</TableCell>
                        <TableCell align="right">Domains</TableCell>
                        <TableCell align="right">Percentage</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!verificationStats?.services || verificationStats.services.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3}>
                            <Alert severity="info">No verification services data available</Alert>
                          </TableCell>
                        </TableRow>
                      ) : (
                        (filteredVerificationServices || verificationStats.services).map((service) => (
                          <TableRow key={service.provider}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box
                                  sx={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    bgcolor: VERIFICATION_SERVICE_COLORS[service.provider] || COLORS[service.provider.length % COLORS.length],
                                    mr: 1
                                  }}
                                />
                                {service.provider}
                              </Box>
                            </TableCell>
                            <TableCell align="right">{service.count}</TableCell>
                            <TableCell align="right">{service.percentage}%</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Domain verification TXT records are used to prove domain ownership when setting up third-party services. 
                    These records typically have specific formats that identify the service they're associated with.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Services Explanation */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Common Verification Services
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>Productivity & Collaboration</Typography>
                    <Typography variant="body2">
                      <strong>Google:</strong> Google Workspace, Google Search Console, Google Analytics
                    </Typography>
                    <Typography variant="body2">
                      <strong>Microsoft:</strong> Microsoft 365, Azure services
                    </Typography>
                    <Typography variant="body2">
                      <strong>Zoom:</strong> Zoom video conferencing
                    </Typography>
                    <Typography variant="body2">
                      <strong>Slack:</strong> Slack workspace setup
                    </Typography>
                    <Typography variant="body2">
                      <strong>Atlassian:</strong> Jira, Confluence, etc.
                    </Typography>
                    <Typography variant="body2">
                      <strong>Airtable:</strong> Airtable database platform
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>Technology & Services</Typography>
                    <Typography variant="body2">
                      <strong>Apple:</strong> Apple Business/School Manager
                    </Typography>
                    <Typography variant="body2">
                      <strong>OpenAI:</strong> GPT/AI service integrations
                    </Typography>
                    <Typography variant="body2">
                      <strong>Stripe:</strong> Payment processing
                    </Typography>
                    <Typography variant="body2">
                      <strong>Shopify:</strong> E-commerce platform
                    </Typography>
                    <Typography variant="body2">
                      <strong>Facebook:</strong> Facebook Workplace, business integrations
                    </Typography>
                    <Typography variant="body2">
                      <strong>Canva:</strong> Graphic design platform
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      {/* Pattern Management Tab */}
      {tabValue === 7 && !loading && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
              <ManageSearchIcon sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
              DNS Verification Pattern Management
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              This section allows you to manage DNS verification patterns that are used to identify verification records for various services.
              These patterns are automatically detected during DNS checks and can be categorized and organized here.
            </Alert>
          </Grid>

          {/* Categories and Recent Patterns Row */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    <CategoryIcon sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
                    Categories
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setCategoryDialogMode('add');
                      setCurrentCategory({ name: '', description: '' });
                      setCategoryFormError(null);
                      setCategoryDialogOpen(true);
                    }}
                  >
                    Add Category
                  </Button>
                </Box>
                
                {loadingCategories ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <>
                    {categoriesList.length === 0 ? (
                      <Alert severity="warning">No categories found. Add one to get started.</Alert>
                    ) : (
                      <List>
                        {categoriesList.map(category => (
                          <ListItem 
                            key={category.id} 
                            sx={{ 
                              borderRadius: 1, 
                              mb: 1, 
                              bgcolor: selectedCategory?.id === category.id ? 'primary.light' : 'background.paper',
                              '&:hover': { bgcolor: 'action.hover' }
                            }}
                          >
                            <ListItemButton onClick={() => setSelectedCategory(category)}>
                              <ListItemIcon>
                                <CategoryIcon />
                              </ListItemIcon>
                              <ListItemText 
                                primary={category.name} 
                                secondary={`${category.pattern_count} patterns`} 
                              />
                            </ListItemButton>
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                setCategoryDialogMode('edit');
                                setCurrentCategory(category);
                                setCategoryFormError(null);
                                setCategoryDialogOpen(true);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmDeleteDialog({
                                  open: true,
                                  type: 'category',
                                  id: category.id,
                                  name: category.name
                                });
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Recent Patterns Card */}
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    <RecentActorsIcon sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
                    Recently Added
                  </Typography>
                  <FormControl size="small" sx={{ width: 120 }}>
                    <InputLabel id="recent-days-label">Last</InputLabel>
                    <Select
                      labelId="recent-days-label"
                      value={recentDays}
                      label="Last"
                      onChange={(e) => {
                        setRecentDays(e.target.value);
                        loadRecentPatterns(e.target.value);
                      }}
                    >
                      <MenuItem value={1}>1 day</MenuItem>
                      <MenuItem value={7}>7 days</MenuItem>
                      <MenuItem value={30}>30 days</MenuItem>
                      <MenuItem value={90}>90 days</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                
                {loadingPatterns ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <>
                    {recentPatterns.length === 0 ? (
                      <Alert severity="info">No patterns have been added in the last {recentDays} day(s).</Alert>
                    ) : (
                      <List>
                        {recentPatterns.slice(0, 10).map(pattern => (
                          <ListItem 
                            key={pattern.id} 
                            sx={{ 
                              borderRadius: 1, 
                              mb: 1,
                              bgcolor: 'background.paper',
                              '&:hover': { bgcolor: 'action.hover' }
                            }}
                            dense
                          >
                            <ListItemText 
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Typography variant="body2" component="span">
                                    {pattern.service}
                                  </Typography>
                                  {!pattern.pattern.includes('=') && (
                                    <Chip
                                      size="small"
                                      label="Irrelevant"
                                      color="error"
                                      variant="outlined"
                                      sx={{ ml: 1, fontSize: '0.7rem' }}
                                    />
                                  )}
                                </Box>
                              }
                              secondary={
                                <Typography variant="caption">
                                  Pattern: {pattern.pattern}<br />
                                  Category: {pattern.category_name}<br />
                                  Added: {new Date(pattern.created_at).toLocaleDateString()}
                                </Typography>
                              } 
                            />
                            <Box sx={{ display: 'flex' }}>
                              <IconButton 
                                size="small" 
                                onClick={() => {
                                  setPatternDialogMode('edit');
                                  setCurrentPattern(pattern);
                                  setPatternFormError(null);
                                  setPatternDialogOpen(true);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={() => {
                                  setConfirmDeleteDialog({
                                    open: true,
                                    type: 'pattern',
                                    id: pattern.id,
                                    name: `${pattern.service} (${pattern.pattern})`
                                  });
                                }}
                                title="Delete pattern"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                              {!pattern.pattern.includes('=') && (
                                <IconButton 
                                  size="small" 
                                  color="warning"
                                  onClick={async () => {
                                    try {
                                      const response = await api.deleteDnsVerificationPattern(pattern.id);
                                      if (response.success) {
                                        showSnackbar(`Discarded irrelevant pattern: ${pattern.pattern}`, 'success');
                                        loadRecentPatterns(recentDays);
                                        loadPatterns();
                                      } else {
                                        showSnackbar(`Failed to discard pattern: ${response.message || 'Unknown error'}`, 'error');
                                      }
                                    } catch (error) {
                                      logger.error("Error discarding pattern:", error);
                                      showSnackbar(`Error discarding pattern: ${error.message || 'Unknown error'}`, 'error');
                                    }
                                  }}
                                  title="Quick discard irrelevant pattern"
                                >
                                  <BlockIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          {/* Patterns List */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    <CodeIcon sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
                    Verification Patterns
                    {selectedCategory && ` - ${selectedCategory.name}`}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {selectedCategory && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setSelectedCategory(null)}
                      >
                        Show All
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setPatternDialogMode('add');
                        setCurrentPattern({ 
                          pattern: '', 
                          service: '', 
                          description: '', 
                          category_id: selectedCategory?.id || null 
                        });
                        setPatternFormError(null);
                        setPatternDialogOpen(true);
                      }}
                    >
                      Add Pattern
                    </Button>
                  </Box>
                </Box>
                
                {/* Search Box */}
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Search patterns"
                    InputProps={{
                      startAdornment: (
                        <Box component="span" sx={{ color: 'action.active', mr: 1 }}>
                          <SearchIcon fontSize="small" />
                        </Box>
                      ),
                    }}
                    placeholder="Search by pattern, service, or description..."
                    onChange={(e) => {
                      const searchTerm = e.target.value.toLowerCase();
                      if (!searchTerm) {
                        setFilteredPatterns(null);
                      } else {
                        // Filter patterns list or the selection by category
                        const patternsToFilter = selectedCategory 
                          ? patternsList.filter(p => p.category_id === selectedCategory.id)
                          : patternsList;
                          
                        const filtered = patternsToFilter.filter(pattern => 
                          pattern.pattern.toLowerCase().includes(searchTerm) ||
                          pattern.service.toLowerCase().includes(searchTerm) ||
                          (pattern.description && pattern.description.toLowerCase().includes(searchTerm))
                        );
                        setFilteredPatterns(filtered);
                      }
                    }}
                  />
                </Box>
                
                {loadingPatterns ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <TableContainer sx={{ maxHeight: 600 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Pattern</TableCell>
                          <TableCell>Service</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell>Category</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* Show either filtered patterns, patterns for selected category, or all patterns */}
                        {(filteredPatterns !== null
                          ? filteredPatterns
                          : selectedCategory
                            ? patternsList.filter(p => p.category_id === selectedCategory.id)
                            : patternsList
                        ).length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              <Alert severity="info">
                                {filteredPatterns !== null 
                                  ? "No patterns match your search."
                                  : selectedCategory
                                    ? `No patterns in the '${selectedCategory.name}' category.`
                                    : "No patterns found in the database."
                                }
                              </Alert>
                            </TableCell>
                          </TableRow>
                        ) : (
                          (filteredPatterns !== null
                            ? filteredPatterns
                            : selectedCategory
                              ? patternsList.filter(p => p.category_id === selectedCategory.id)
                              : patternsList
                          ).map(pattern => (
                            <TableRow key={pattern.id}>
                              <TableCell>
                                <Tooltip title="Pattern used to match DNS records">
                                  <Typography fontFamily="monospace" sx={{ fontWeight: 'bold' }}>
                                    {pattern.pattern}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              <TableCell>{pattern.service}</TableCell>
                              <TableCell>{pattern.description}</TableCell>
                              <TableCell>
                                <Chip 
                                  size="small" 
                                  label={pattern.category_name}
                                  color="primary"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="center">
                                <IconButton 
                                  size="small" 
                                  onClick={() => {
                                    setPatternDialogMode('edit');
                                    setCurrentPattern(pattern);
                                    setPatternFormError(null);
                                    setPatternDialogOpen(true);
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton 
                                  size="small" 
                                  color="error" 
                                  onClick={() => {
                                    setConfirmDeleteDialog({
                                      open: true,
                                      type: 'pattern',
                                      id: pattern.id,
                                      name: `${pattern.pattern} (${pattern.service})`
                                    });
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
            
          {/* Pattern Edit/Add Dialog */}
          <Dialog 
            open={patternDialogOpen} 
            onClose={() => setPatternDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              {patternDialogMode === 'add' ? 'Add New Pattern' : 'Edit Pattern'}
            </DialogTitle>
            <DialogContent>
              {patternFormError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {patternFormError}
                </Alert>
              )}
              
              <TextField
                label="Pattern"
                fullWidth
                margin="normal"
                value={currentPattern?.pattern || ''}
                onChange={(e) => setCurrentPattern({...currentPattern, pattern: e.target.value})}
                required
                helperText="The text pattern to match in DNS records (e.g. 'google-', 'MS=', etc.)"
              />
              
              <TextField
                label="Service"
                fullWidth
                margin="normal"
                value={currentPattern?.service || ''}
                onChange={(e) => setCurrentPattern({...currentPattern, service: e.target.value})}
                required
                helperText="The service this pattern is associated with (e.g. 'Google', 'Microsoft', etc.)"
              />
              
              <TextField
                label="Description"
                fullWidth
                margin="normal"
                value={currentPattern?.description || ''}
                onChange={(e) => setCurrentPattern({...currentPattern, description: e.target.value})}
                helperText="Optional description of what this pattern is used for"
              />
              
              <FormControl fullWidth margin="normal" required>
                <InputLabel id="category-select-label">Category</InputLabel>
                <Select
                  labelId="category-select-label"
                  value={currentPattern?.category_id || ''}
                  onChange={(e) => setCurrentPattern({...currentPattern, category_id: e.target.value})}
                  label="Category"
                >
                  {categoriesList.map(category => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Select the category this pattern belongs to</FormHelperText>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPatternDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSavePattern}
                disabled={
                  !currentPattern?.pattern ||
                  !currentPattern?.service ||
                  !currentPattern?.category_id
                }
              >
                Save Pattern
              </Button>
            </DialogActions>
          </Dialog>
          
          {/* Category Edit/Add Dialog */}
          <Dialog 
            open={categoryDialogOpen} 
            onClose={() => setCategoryDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              {categoryDialogMode === 'add' ? 'Add New Category' : 'Edit Category'}
            </DialogTitle>
            <DialogContent>
              {categoryFormError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {categoryFormError}
                </Alert>
              )}
              
              <TextField
                label="Category Name"
                fullWidth
                margin="normal"
                value={currentCategory?.name || ''}
                onChange={(e) => setCurrentCategory({...currentCategory, name: e.target.value})}
                required
                helperText="Name of the category (e.g. 'Cloud Services', 'Email', etc.)"
              />
              
              <TextField
                label="Description"
                fullWidth
                margin="normal"
                value={currentCategory?.description || ''}
                onChange={(e) => setCurrentCategory({...currentCategory, description: e.target.value})}
                helperText="Optional description of what this category includes"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCategoryDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSaveCategory}
                disabled={!currentCategory?.name}
              >
                Save Category
              </Button>
            </DialogActions>
          </Dialog>
          
          {/* Confirm Delete Dialog */}
          <Dialog
            open={confirmDeleteDialog.open}
            onClose={() => setConfirmDeleteDialog({...confirmDeleteDialog, open: false})}
          >
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to delete this {confirmDeleteDialog.type}?
              </Typography>
              <Typography fontWeight="bold" mt={1}>
                {confirmDeleteDialog.name}
              </Typography>
              {confirmDeleteDialog.type === 'category' && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  All patterns in this category will also be deleted!
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={() => setConfirmDeleteDialog({...confirmDeleteDialog, open: false})}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                color="error"
                onClick={handleConfirmDelete}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </Grid>
      )}
      
      {/* Bulk DNS Checks Tab */}
      {tabValue === 8 && (
        <Grid container spacing={3}>
          {/* Statistics Cards */}
          <Grid item xs={12}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                <TimelineIcon sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
                MX Provider Statistics
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PlayArrowIcon />}
                onClick={handleStartBulkCheck}
                disabled={bulkCheckLoading || !!activeBulkOperation}
              >
                Start Bulk MX Check
              </Button>
            </Box>
            
            {/* Error message */}
            {bulkCheckError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {bulkCheckError}
              </Alert>
            )}
            
            {/* Loading indicator */}
            {bulkCheckLoading && !bulkStats && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            )}
            
            {bulkStats && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={2}>
                  <Card sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                    <CardContent>
                      <Typography variant="h5" gutterBottom>
                        {bulkStats.total_records}
                      </Typography>
                      <Typography variant="body2">Total Records</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <Card sx={{ bgcolor: 'success.main', color: 'success.contrastText' }}>
                    <CardContent>
                      <Typography variant="h5" gutterBottom>
                        {bulkStats.records_with_url}
                      </Typography>
                      <Typography variant="body2">Records with URL</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <Card sx={{ bgcolor: 'warning.main', color: 'warning.contrastText' }}>
                    <CardContent>
                      <Typography variant="h5" gutterBottom>
                        {bulkStats.records_without_url}
                      </Typography>
                      <Typography variant="body2">Records without URL</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <Card sx={{ bgcolor: 'info.main', color: 'info.contrastText' }}>
                    <CardContent>
                      <Typography variant="h5" gutterBottom>
                        {bulkStats.records_already_checked}
                      </Typography>
                      <Typography variant="body2">Domains Already Checked</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <Card sx={{ bgcolor: 'error.main', color: 'error.contrastText' }}>
                    <CardContent>
                      <Typography variant="h5" gutterBottom>
                        {bulkStats.records_to_check}
                      </Typography>
                      <Typography variant="body2">New Domains To Check</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <Card sx={{ bgcolor: '#9c27b0', color: 'white' }}>
                    <CardContent>
                      <Typography variant="h5" gutterBottom>
                        {bulkStats.domains_to_recheck?.[30] || 0}
                      </Typography>
                      <Typography variant="body2">Domains To Recheck (30+ days)</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                {/* Recheck info cards for different time periods */}
                <Grid item xs={12}>
                  <Card sx={{ mt: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Domains Due for Recheck:
                      </Typography>
                      <Grid container spacing={2}>
                        {/* Hour-based intervals */}
                        {bulkStats.domains_to_recheck && ['1h', '4h', '8h'].map(hoursKey => (
                          <Grid item xs={6} sm={3} key={hoursKey}>
                            <Box sx={{ 
                              p: 2, 
                              borderRadius: 2, 
                              bgcolor: 'background.paper',
                              border: '1px solid',
                              borderColor: 'divider',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              boxShadow: 1
                            }}>
                              <Typography variant="h5" color="primary.main" sx={{ fontWeight: 'bold' }}>
                                {bulkStats.domains_to_recheck[hoursKey] || 0}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Not checked in {hoursKey.replace('h', '+ hours')}
                              </Typography>
                              <Button 
                                variant="outlined" 
                                size="small" 
                                sx={{ mt: 1 }}
                                disabled={!bulkStats.domains_to_recheck[hoursKey] || bulkCheckLoading || !!activeBulkOperation}
                                onClick={() => {
                                  setCheckMode('recheck');
                                  setDaysOld(hoursKey);
                                  handleStartBulkCheck();
                                }}
                              >
                                Recheck
                              </Button>
                            </Box>
                          </Grid>
                        ))}
                        {/* Day-based intervals */}
                        {bulkStats.domains_to_recheck && [1, 3, 7, 30].map(days => (
                          <Grid item xs={6} sm={3} key={days}>
                            <Box sx={{ 
                              p: 2, 
                              borderRadius: 2, 
                              bgcolor: 'background.paper',
                              border: '1px solid',
                              borderColor: 'divider',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              boxShadow: 1
                            }}>
                              <Typography variant="h5" color="primary.main" sx={{ fontWeight: 'bold' }}>
                                {bulkStats.domains_to_recheck[days] || 0}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Not checked in {days}+ days
                              </Typography>
                              <Button 
                                variant="outlined" 
                                size="small" 
                                sx={{ mt: 1 }}
                                disabled={!bulkStats.domains_to_recheck[days] || bulkCheckLoading || !!activeBulkOperation}
                                onClick={() => {
                                  setCheckMode('recheck');
                                  setDaysOld(days);
                                  handleStartBulkCheck();
                                }}
                              >
                                Recheck
                              </Button>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </Grid>
          
          {/* Active Operation Section */}
          {activeBulkOperation && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      Active MX Check Operation
                    </Typography>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<StopIcon />}
                      onClick={() => cancelBulkCheck(activeBulkOperation.id)}
                      disabled={bulkCheckLoading}
                    >
                      Cancel Operation
                    </Button>
                    <Button
                      variant="outlined"
                      color="info"
                      onClick={() => activeBulkOperation && forceStartPolling(activeBulkOperation.id)}
                      sx={{ ml: 1 }}
                    >
                      Refresh Status
                    </Button>
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="text.secondary">Operation ID:</Typography>
                      <Typography variant="body1">{activeBulkOperation.id}</Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="text.secondary">Status:</Typography>
                      <Chip
                        label={activeBulkOperation.status}
                        color={
                          activeBulkOperation.status === 'completed' ? 'success' :
                          activeBulkOperation.status === 'running' ? 'primary' :
                          activeBulkOperation.status === 'queued' ? 'info' :
                          activeBulkOperation.status === 'cancelled' ? 'warning' : 'error'
                        }
                        icon={
                          activeBulkOperation.status === 'completed' ? <CheckIcon /> :
                          activeBulkOperation.status === 'running' ? <PlayArrowIcon /> :
                          activeBulkOperation.status === 'queued' ? <PauseIcon /> :
                          activeBulkOperation.status === 'cancelled' ? <StopIcon /> : <ErrorIcon />
                        }
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="text.secondary">Started By:</Typography>
                      <Typography variant="body1">{activeBulkOperation.created_by}</Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="text.secondary">Start Time:</Typography>
                      <Typography variant="body1">
                        {new Date(activeBulkOperation.start_time).toLocaleString()}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Progress:</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: '100%', mr: 1 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={activeBulkOperation.percent_complete} 
                            sx={{ height: 10, borderRadius: 5 }}
                          />
                        </Box>
                        <Box sx={{ minWidth: 35 }}>
                          <Typography variant="body2" color="text.secondary">
                            {`${Math.round(activeBulkOperation.percent_complete)}%`}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                        {activeBulkOperation.domains_checked} of {activeBulkOperation.total_domains} domains checked
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}
          
          {/* Sequential Mode Status Display */}
          {sequentialMode && sequentialStats.isActive && (
            <Grid item xs={12}>
              <Card sx={{ mt: 2, mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      Sequential Operation Progress
                    </Typography>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<StopIcon />}
                      onClick={cancelSequentialOperations}
                    >
                      Cancel Sequence
                    </Button>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Processing all domains in batches of 1000
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={(sequentialStats.completedOperations / sequentialStats.totalOperations) * 100} 
                          sx={{ height: 10, borderRadius: 5 }}
                        />
                      </Box>
                      <Box sx={{ ml: 2 }}>
                        <Typography variant="body2">
                          {sequentialStats.completedOperations} of {sequentialStats.totalOperations} operations
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
                        <Typography variant="h6">{sequentialStats.totalDomainsChecked}</Typography>
                        <Typography variant="body2" color="text.secondary">Domains Checked</Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
                        <Typography variant="h6">{sequentialStats.remainingDomains}</Typography>
                        <Typography variant="body2" color="text.secondary">Domains Remaining</Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
                        <Typography variant="h6">{sequentialStats.completedOperations}</Typography>
                        <Typography variant="body2" color="text.secondary">Batches Completed</Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
                        <Typography variant="h6">{sequentialStats.totalOperations - sequentialStats.completedOperations}</Typography>
                        <Typography variant="body2" color="text.secondary">Batches Remaining</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}
          
          {/* Past Operations Section */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  MX Check Operations History
                </Typography>
                
                {bulkCheckOperations.length === 0 ? (
                  <Alert severity="info">No MX check operations found.</Alert>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Started</TableCell>
                          <TableCell>Started By</TableCell>
                          <TableCell align="right">Domains</TableCell>
                          <TableCell align="right">Progress</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bulkCheckOperations.map((operation) => (
                          <TableRow key={operation.id}>
                            <TableCell>
                              <Tooltip title={operation.id}>
                                <Typography variant="body2">
                                  {operation.id.substring(0, 8)}...
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={operation.status}
                                color={
                                  operation.status === 'completed' ? 'success' :
                                  operation.status === 'running' ? 'primary' :
                                  operation.status === 'queued' ? 'info' :
                                  operation.status === 'cancelled' ? 'warning' : 'error'
                                }
                              />
                            </TableCell>
                            <TableCell>
                              {new Date(operation.start_time).toLocaleString()}
                            </TableCell>
                            <TableCell>{operation.created_by}</TableCell>
                            <TableCell align="right">
                              {operation.domains_checked} / {operation.total_domains}
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ width: '100%', mr: 1 }}>
                                  <LinearProgress 
                                    variant="determinate" 
                                    value={operation.percent_complete} 
                                    sx={{ height: 8, borderRadius: 4 }}
                                  />
                                </Box>
                                <Box sx={{ minWidth: 35 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    {`${Math.round(operation.percent_complete)}%`}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              {(operation.status === 'running' || operation.status === 'queued') && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() => cancelBulkCheck(operation.id)}
                                  disabled={bulkCheckLoading}
                                >
                                  Cancel
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      {/* Start Bulk Check Dialog */}
      <Dialog 
        open={startBulkCheckDialogOpen} 
        onClose={() => setStartBulkCheckDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Start Bulk MX Check</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              This will check MX records for domains in the database. You can choose to check new domains 
              or recheck domains that haven't been updated recently.
            </Typography>
            
            {bulkStats && (
              <Box sx={{ mb: 3, mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Available Domains for Check:</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Alert severity="info" sx={{ mb: 1 }}>
                      <Typography variant="body2">
                        <strong>{bulkStats.records_to_check}</strong> new domains available
                      </Typography>
                    </Alert>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      <Typography variant="body2">
                        <strong>{bulkStats.domains_to_recheck?.[daysOld] || 0}</strong> domains not checked in {daysOld} days
                      </Typography>
                    </Alert>
                  </Grid>
                </Grid>
              </Box>
            )}
            
            {/* Check Mode Selection */}
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>Check Mode:</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mb: 2 }}>
                <Button
                  variant={checkMode === 'new' ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={() => setCheckMode('new')}
                  sx={{ flexGrow: 1 }}
                >
                  <Box sx={{ p: 1 }}>
                    <Typography variant="button">New Domains</Typography>
                    <Typography variant="caption" display="block">
                      Check domains never checked before
                    </Typography>
                  </Box>
                </Button>
                
                <Button
                  variant={checkMode === 'recheck' ? 'contained' : 'outlined'} 
                  color="secondary"
                  onClick={() => setCheckMode('recheck')}
                  sx={{ flexGrow: 1 }}
                >
                  <Box sx={{ p: 1 }}>
                    <Typography variant="button">Recheck Domains</Typography>
                    <Typography variant="caption" display="block">
                      Refresh domains checked previously
                    </Typography>
                  </Box>
                </Button>
              </Box>
            </FormControl>
            
            {/* Add Sequential Check Option */}
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={sequentialMode}
                    onChange={(e) => setSequentialMode(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle2">Process All Domains Sequentially (Backend)</Typography>
                    <Typography variant="caption" color="text.secondary">
                      The backend will handle all domains in batches of 1000 until all are checked
                    </Typography>
                  </Box>
                }
              />
              
              {sequentialMode && bulkStats && (
                <Box sx={{ mt: 1, p: 2, bgcolor: 'info.main', color: 'info.contrastText', borderRadius: 1 }}>
                  <Typography variant="body2">
                    {checkMode === 'new' ? (
                      <>
                        Will process all {bulkStats.records_to_check} new domains in {Math.ceil(bulkStats.records_to_check / 1000)} operations
                      </>
                    ) : (
                      <>
                        Will process all {bulkStats.domains_to_recheck?.[daysOld] || 0} domains older than {typeof daysOld === 'string' ? daysOld.replace('h', ' hour') + (daysOld.startsWith('1') ? '' : 's') : `${daysOld} days`} in {Math.ceil((bulkStats.domains_to_recheck?.[daysOld] || 0) / 1000)} operations
                      </>
                    )}
                  </Typography>
                </Box>
              )}
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="max-domains-label">Maximum Domains to Check</InputLabel>
                  <Select
                    labelId="max-domains-label"
                    value={maxDomainsToCheck}
                    onChange={(e) => setMaxDomainsToCheck(e.target.value)}
                    label="Maximum Domains to Check"
                    disabled={sequentialMode}
                  >
                    <MenuItem value={50}>50 domains</MenuItem>
                    <MenuItem value={100}>100 domains</MenuItem>
                    <MenuItem value={250}>250 domains</MenuItem>
                    <MenuItem value={500}>500 domains</MenuItem>
                    <MenuItem value={1000}>1000 domains (maximum)</MenuItem>
                  </Select>
                  <FormHelperText>
                    {sequentialMode 
                      ? "In sequential mode, each operation processes 1000 domains" 
                      : "Backend limit: maximum 1000 domains per operation"}
                  </FormHelperText>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Batch Size"
                  type="number"
                  fullWidth
                  value={batchSize}
                  onChange={(e) => setBatchSize(parseInt(e.target.value) || 10)}
                  InputProps={{ inputProps: { min: 1, max: 50 } }}
                  helperText="Number of domains to check in parallel (1-50)"
                />
              </Grid>
              
              {/* Days old selector (only shown when 'recheck' is selected) */}
              {checkMode === 'recheck' && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Select domains older than:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {/* Hour-based intervals */}
                    {['1h', '4h', '8h'].map((interval) => (
                      <Chip
                        key={interval}
                        label={interval.replace('h', ' hour') + (interval.startsWith('1') ? '' : 's')}
                        color={daysOld === interval ? 'primary' : 'default'}
                        onClick={async () => {
                          setDaysOld(interval);
                          setBulkCheckLoading(true);
                          try {
                            const response = await api.getDnsBulkStats();
                            if (response.success) {
                              setBulkStats(response.data);
                            }
                          } finally {
                            setBulkCheckLoading(false);
                          }
                        }}
                        sx={{ px: 1 }}
                        variant={daysOld === interval ? 'filled' : 'outlined'}
                      />
                    ))}
                    {/* Day-based intervals */}
                    {[1, 3, 7, 30].map((days) => (
                      <Chip
                        key={days}
                        label={`${days} day${days === 1 ? '' : 's'}`}
                        color={daysOld === days ? 'primary' : 'default'}
                        onClick={async () => {
                          setDaysOld(days);
                          setBulkCheckLoading(true);
                          try {
                            const response = await api.getDnsBulkStats();
                            if (response.success) {
                              setBulkStats(response.data);
                            }
                          } finally {
                            setBulkCheckLoading(false);
                          }
                        }}
                        sx={{ px: 1 }}
                        variant={daysOld === days ? 'filled' : 'outlined'}
                      />
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    This will recheck domains that haven't been updated in {typeof daysOld === 'string' ? daysOld.replace('h', ' hour') + (daysOld.startsWith('1') ? '' : 's') : `${daysOld} days`} or more.
                    {bulkStats && bulkStats.domains_to_recheck &&
                      (() => {
                        const count = bulkStats.domains_to_recheck[daysOld] || 0;
                        return ` Found ${count} domains to recheck.`;
                      })()
                    }
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartBulkCheckDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={startBulkCheck}
            disabled={
              bulkCheckLoading || 
              (checkMode === 'recheck' && bulkStats?.domains_to_recheck?.[daysOld] === 0) ||
              (sequentialMode && (
                (checkMode === 'new' && bulkStats?.records_to_check === 0) ||
                (checkMode === 'recheck' && bulkStats?.domains_to_recheck?.[daysOld] === 0)
              ))
            }
            startIcon={bulkCheckLoading ? <CircularProgress size={20} /> : <PlayArrowIcon />}
          >
            {sequentialMode ? 'Start Backend Sequential Check' : 'Start Check'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity} 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DNSStatistics; 